<template>
  <div class="content">
    <div class="page-inner">
      <div class="page-header mb-0">
        <router-link to="/distributions">
          <i class="fa fa-chevron-left mr-1"></i>
          {{ $t('my_releases') }}
        </router-link>
      </div>
      <DistributionProgress :distribution="distribution"></DistributionProgress>
      <div>
        <div>
          <router-link to="/tools/mastering" target="_blank" class="btn btn-link btn-block">
            {{ $t('mastering_service')}} <i class="fas fa-external-link-alt ml-2"></i>
          </router-link>
        </div>

        <div :class="'mt-4 card mb-2 ' + ((trackSectionFlags[index]) ? 'active' : '')" v-for="(track, index) in distribution.songs" v-bind:key="`track_${index}`">
          <div :id="'song_' + index" class="card-header btn header-track-section w-100" @click="toggleTrackSection(index)"  data-toggle="collapse" :data-target="`#track-container-${index}`" aria-expanded="false" :aria-controls="`track-container-${index}`">
            <div class="strong float-left h4 text-left track-title-container">
              <span v-html="(index + 1) + '. ' + (track.title ? track.title : '<i style=\'color: #757575;\'>[' + $t('add_track_title') + ']</i>')"></span>
            </div>
            <div class="float-right text-right track-buttons-container">
              <button type="button" class="btn btn-danger btn-sm mr-2" v-show="index > 0" @click="removeSong(track, index)">
                <i class="fa fa-trash"></i>
              </button>
              <div class="display-inline">
                <i class="fas mt-2 fasAnimate fa-plus"></i>
                <i class="fas mt-2 fasAnimate fa-minus"></i>
              </div>
            </div>
          </div>
          <div :class="'collapse animated fadeIn' + (index === 0) ? 'show' : ''" :id="`track-container-${index}`">
            <div class="card-body">
              <div>
                <span class="text-small text-muted ml-2">{{ $t('file') }}</span>
              </div>
              <!-- Files -->
              <div class="row">
                <div class="col-md-6">
                  <div class="form-group">
                    <label for>
                      {{ $t('audio_file') }}
                      <span class="required-label">*</span>
                    </label>
                    <div class="input-group">
                      <div v-if="track.file_url" class="w-100">
                        <div class="w-100">
                          <audio
                            :ref="`audio_${index}`"
                            controls
                            preload="metadata"
                            metadata="metadata"
                            :id="`audio_${index}`"
                            @loadedmetadata="onLoadedMetadata(track, index)"
                            @timeupdate="onTimeUpdate(track, index)"
                            @loadstart="onAudioLoad(index)"
                          >
                            <source
                              :src="track.file_url"
                              type="audio/wav"
                              v-if="track.file_url.match(/\.wav$/, 'i')"
                            />
                            <source
                              :src="track.file_url"
                              type="audio/mp3"
                              v-if="track.file_url.match(/\.mp3$/, 'i')"
                            />
                            {{ $t('wrong_audio_element') }}
                          </audio>
                          <span class="btn btn-sm text-danger float-right replace-audio-icon" @click="replaceAudio(track, index)">
                            <i class="fas fa-trash-alt"></i>
                          </span>
                        </div>
                      </div>
                      <div class="input-file col-md-6 not-pl" v-show="!track.file_url && !track.uploading">
                        <div>
                          <input
                            :ref="`file_${index}`"
                            @change="uploadFile(track, index)"
                            type="file"
                            :id="`songfile_${index}`"
                            class="form-control form-control-file w-100"
                            accept=".wav, .mp3"
                            required
                          />
                          <label
                            :for="`songfile_${index}`"
                            class="label-input-file btn btn-primary rds4 w-100"
                          >
                            <span class="btn-label">
                              <i class="fa fa-file-audio mr-1"></i>
                            </span>
                            {{ $t('select_file') }}
                          </label>
                        </div>
                      </div>
                      <div v-show="!track.file_url && !track.uploading" class="audio-type">{{ $t('mp3_file_desc') }}</div>
                      <div v-show="track.uploading && !track.file_url" class="w-100">
                        <div class="progress mt-3 progress-sm">
                          <div
                            class="progress-bar bg-primary progress-bar-striped progress-bar-animated"
                            role="progressbar"
                            :style="`width: ${track.uploading}%`"
                            :aria-valuenow="track.uploading"
                            aria-valuemin="0"
                            aria-valuemax="100"
                            data-toggle="tooltip"
                            data-placement="top"
                            title
                            :data-original-title="`${track.uploading}%`"
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="p-2 text-muted">
                    {{ $t('recommended_encoding') }}
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="form-group" v-if="track.file_name" :class="metadataChangeClass[index + '_file'] ? 'metadata-modified' : ''">
                    <label class="mb-0">{{ $t('file_info') }}</label>
                    <div v-if="track.file_name">
                      <div>
                        <span class="small"><span class="strong">Name:</span> {{ track.file_name }}</span>
                      </div>
                      <div>
                        <span class="small"><span class="strong">Size:</span> {{ track.file_size }} MB</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="form-group">
                    <label for>ISRC</label>
                    <input
                      type="text"
                      class="form-control"
                      @change="validateISRC(track, index)"
                      v-model="track.isrc"
                      :placeholder="$t('optional')"
                      :class="metadataChangeClass[index + '_isrc'] ? 'metadata-modified' : ''"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div class="card-body bg-2">
              <!-- Main Info -->
              <div>
                <span class="text-small text-muted ml-2">{{ $t('track_information') }}</span>
              </div>
              <div class="row mb-4">
                <div class="col-md-3">
                  <div class="form-group">
                    <label for>
                      {{ $t('track_title') }}
                      <span class="required-label">*</span>
                    </label>
                    <input
                      :readonly="distribution.release_type === 'single'"
                      type="text"
                      class="form-control"
                      v-model="track.title"
                      required
                      @change="validateSongTitle(track, index)"
                      @input="handleInputTrackTitle(track)"
                      @blur="validateSongTitle(track, index)"
                      @paste.prevent
                      :class="metadataChangeClass[index + '_title'] ? 'metadata-modified' : ''"
                    />
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="form-group">
                    <label for>{{ $t('track_version') }}</label>
                    <div class="select2-input" :class="metadataChangeClass[index + '_version'] ? 'metadata-modified' : ''">
                      <Select2 v-model="track.version" :options="versions"></Select2>
                    </div>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="form-group">
                    <label for>
                      {{ $t('main_genre') }}
                      <span class="required-label">*</span>
                    </label>
                    <div class="select2-input" :class="metadataChangeClass[index + '_main_genre'] ? 'metadata-modified' : ''">
                      <Select2
                        :disabled="distribution.release_type === 'single'"
                        v-model="track.main_genre"
                        :options="genres"
                      ></Select2>
                    </div>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="form-group">
                    <label for>
                      {{ $t('style') }}
                      <span class="required-label">*</span>
                    </label>
                    <div class="select2-input" :class="metadataChangeClass[index + '_style'] ? 'metadata-modified' : ''">
                      <Select2
                        :disabled="distribution.release_type === 'single'"
                        v-model="track.style"
                        :options="genresSource[track.main_genre]"
                        v-if="track.main_genre"
                      ></Select2>
                    </div>
                  </div>
                </div>
              </div>
              <div class="row contain-vocals">
                <div class="col-md-3">
                  <div class="form-group" :class="metadataChangeClass[index + '_hasLyrics'] ? 'metadata-modified' : ''">
                    <label for="">{{ $t('contains_lyrics') }}<span class="required-label">*</span></label>
                    <div>
                      <div class="selectgroup w-50">
                        <label class="selectgroup-item mb-0">
                          <input type="radio" :name="`checkLyrics${index}`" value="1" class="selectgroup-input" @click="setLyrics(index)">
                          <span class="selectgroup-button">{{ $t('yes') }}</span>
                        </label>
                        <label class="selectgroup-item mb-0">
                          <input type="radio" :name="`checkLyrics${index}`" value="0" class="selectgroup-input" @click="setLyrics(index)">
                          <span class="selectgroup-button">{{ $t('no') }}</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="form-group" v-if="track.hasLyrics">
                    <label for>{{ $t('lyrics_language') }}</label>
                    <div class="select2-input" :class="metadataChangeClass[index + '_performance_language'] ? 'metadata-modified' : ''">
                      <Select2 v-model="track.performance_language" :options="performanceLanguages"></Select2>
                    </div>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="form-group" v-show="track.hasLyrics" :class="metadataChangeClass[index + '_explicit'] ? 'metadata-modified' : ''">
                    <label for="">{{ $t('is_explicit') }}</label>
                    <div>
                      <div @click="setExplicit(track, index)" class="display-inline">
                        <input type="checkbox" :id="`checkExplicit${index}`" data-toggle="toggle" data-onstyle="primary" data-style="btn-round" class="toggle-checkbox" data-size="small">
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="form-group" v-show="track.hasLyrics" :class="metadataChangeClass[index + '_lyrics'] ? 'metadata-modified' : ''">
                    <div class="form-group">
                      <label for="">{{ $t('lyrics') }}</label>
                      <textarea
                        @change="validateSongLyrics(track, index)"
                        @keypress="validateSongLyrics(track, index)"
                        @blur="validateSongLyrics(track, index)"
                        @paste="validateSongLyrics(track, index)"
                        v-model="track.lyrics"
                        cols="30"
                        rows="10"
                        required="required"
                        class="form-control"
                        :placeholder="`Grey skies, frownin' at me\nGrey skies I watch\nRedbirds yellin' a tune\nGrey skies I watch all day long\n\nCan't see the moon shining so dull\nCan't see the moon shining so dull\nCan't the days hurry by\nCan't the days hurry by\n\nCan't see the moon shining so dull\nCan't see the moon shining so dull\nCan't the days hurry by\nCan't the days hurry by`"
                      ></textarea>
                    </div>
                  </div>
                </div>
                <div class="col-md-12" v-if="track.file_url">
                  <div class="form-group mt-2">
                    <label for="" class="fw-bold text-primary">TikTok / Instagram Preview <span class="ml-1 badge badge-pro badge-primary">PRO</span></label>
                    <div class="mt-1">
                      <p>{{ $t('social_preview_description') }}</p>
                      <div class="mt-2" v-if="!track.custom_preview">
                        <button type="button" class="btn btn-light" @click="initWaveform(index)">
                          {{ $t('choose_preview_loop') }} <i class="fa fa-headphones ml-1"></i>
                        </button>
                      </div>
                    </div>
                    <div class="form-group" v-show="track.custom_preview">
                      <div :ref="`wavesurfer_${index}`"></div>
                      <p v-if="!wavesurfer[index] || (wavesurfer[index] && !wavesurfer[index].loaded)" class="mt-3">
                        <i class="fa fa-spinner fa-spin mr-2"></i> {{ $t('downloading_preview') }}
                      </p>
                      <div class="mt-3">
                        <button v-if="wavesurfer[index] && wavesurfer[index].loaded" type="button" class="btn btn-light" @click="playPreview(index)">
                          <span v-if="wavesurfer[index].playing">
                            <i class="fa fa-pause mr-2"></i> {{ $t('pause') }}
                          </span>
                          <span v-if="!wavesurfer[index].playing">
                            <i class="fa fa-play mr-2"></i> {{ $t('play')}}
                          </span>
                      </button>
                      <button v-if="track.preview_end" class="btn btn-light ml-2" type="button" disabled>
                        {{ track.preview_start }} | {{ track.preview_end }}
                      </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <button type="button" class="btn btn-block" @click="toggleContributorsView(index)" :class="{ 'active': visibleContributors[index] }">
                {{ $t('contributors') }} <i class="fa text-muted ml-1" :class="{ 'fa-chevron-up': visibleContributors[index], 'fa-chevron-down': !visibleContributors[index] }"></i>
              </button>
            </div>

            <div class="card-header header-track-section w-100 strong h4 card-body pl-4" v-show="visibleContributors[index]">
              <div class="row">
                <div class="text-center pt-3 col-12 col-md-4 col-sm-4 col-lg-3 text-lg-left text-md-left title-elemnt">
                  <span class="icon-card">
                    <i class="flaticon-user mr-1"></i>
                  </span>
                  {{ $t('featured_artists') }}
                  <span class="text-14 text-modified" v-if="metadataChangeClass[index + '_featuring']"><i class="ml-2 fas fa-circle"></i></span>
                </div>
                <div class="text-center col-12 col-md-8 col-sm-8 col-lg-9 text-lg-right text-md-right">
                  <span class="more-info-link" v-html="$t('more_info_edit_distro')"></span>
                  <button type="button" class="btn btn-gray" @click="showFeaturingModal(index)" data-dismiss="modal">
                    <i class="fas fa-plus mr-2"></i>{{ $t('add_new') }}
                  </button>
                  <span class="more-info-link mobile" v-html="$t('more_info_edit_distro')"></span>
                </div>
              </div>
            </div>

            <div class="card-body" v-if="track.featuring && track.featuring.length && visibleContributors[index]">
              <div class="row">
                <div class="col-md-12">
                  <div class="row">
                    <div class="col-md-4 mt-2" v-for="(featuring, featuringIndex) in track.featuring" v-bind:key="`featuring_${featuringIndex}`">
                      <div class="card mb-0 pos-relative already-spotify">
                        <div class="card-body pt-2 pb-2">
                          <div class="card-list p-0" :class="{'mt-2': !(featuring.primaryArtist)}">
                            <div class="item-list p-0">
                              <div class="avatar-sm rounded-circle" :class="{'dark': !(featuring.details && featuring.details.external_urls)}">
                                <a :href="featuring.details.external_urls" target="_blank" v-if="featuring.details && featuring.details.external_urls">
                                  <img :src="featuring.details.img" :alt="featuring.name" class="avatar-img rounded-circle">
                                </a>
                                <img :src="defaultImageBlack" :alt="featuring.name" class="defaultProfile avatar-img p-1" v-else>
                              </div>
                              <div class="info-user mt-2 ml-3">
                                <div class="h4 mb-0">
                                  <a class="title-artist" :href="featuring.details.external_urls" target="_blank" v-if="featuring.details && featuring.details.external_urls">
                                    <span>Ft.</span> {{ featuring.name }}
                                  </a>
                                  <span class="title-artist" v-else>
                                    <span>Ft.</span> {{ featuring.name }}
                                  </span>
                                </div>
                                <div class="h5 text-muted followers m-0" v-if="featuring.details && featuring.details.followers">{{ featuring.details.followers }} {{ $t('followers') }}</div>
                                <div class="h5 text-muted followers m-0" v-else>&nbsp;</div>
                                <div class="is-principal h3">
                                  {{ (featuring.primaryArtist) ? $t('primary_artist') : '' }}
                                </div>
                              </div>
                              <span class="btn btn-sm remove-contributor-icon" @click="removeFeaturing(track, featuringIndex, index)">
                                <i class="fas fa-times"></i>
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="card-header header-track-section w-100 strong h4 card-body pl-4" v-show="visibleContributors[index]">
              <div class="row">
                <div class="text-center pt-3 col-12 col-md-6 col-sm-6 col-lg-5 text-lg-left text-md-left title-elemnt">
                  <span class="icon-card">
                    <i class="flaticon-user"></i>
                  </span>
                  {{ $t('composers_contributors') }}
                  <span class="text-14 text-modified" v-if="metadataChangeClass[index + '_contributors']"><i class="ml-2 fas fa-circle"></i></span>
                </div>
                <div class="text-center col-12 col-md-6 col-sm-6 col-lg-7 text-lg-right text-md-right">
                  <span class="more-info-link" v-html="$t('more_info_edit_distro')"></span>
                  <button type="button" class="btn btn-gray" @click="showContributirModal(index)" data-dismiss="modal">
                    <i class="fas fa-user mr-2"></i>{{ $t('add_new') }}
                  </button>
                  <span class="more-info-link mobile" v-html="$t('more_info_edit_distro')"></span>
                </div>
              </div>
            </div>

            <div class="card-body" v-show="visibleContributors[index]">
              <div class="row">
                <div class="col-md-12">
                  <div class="row" v-if="track.contributors">
                    <div class="col-md-4 mt-2" v-for="(contributor, contributorIndex) in track.contributors.data" v-bind:key="`contributor_${contributorIndex}`">
                      <div class="card mb-0 pos-relative already-spotify">
                        <div class="card-body pt-2 pb-2">
                          <div class="card-list p-0 mt-2">
                            <div class="item-list p-0">
                              <div class="info-user mt-2 ml-3">
                                <div class="h4 mb-0">
                                  <span class="title-artist pointer" @click="showContributirModal(index, contributor, contributorIndex)">
                                    <i class="fa fa-edit fa-2x mr-1"></i> {{ contributor.name }}
                                  </span>
                                </div>
                                <div class="h5 text-muted followers m-0">
                                  <span>{{ contributor.role }}</span>
                                  <span>- {{ contributor.percentage || 0 }}%</span>
                                </div>
                                <div class="is-principal h3" v-if="contributor.details && contributor.details.followers">
                                  {{ contributor.role }}
                                </div>
                              </div>
                              <span class="btn btn-sm remove-contributor-icon" @click="removeContributor(track, contributorIndex, index)" v-if="!validateRemoveContributor(contributor, track.contributors.data.length) && (contributorIndex !== 0 && firstLyricist !== contributorIndex)">
                                <i class="fas fa-times"></i>
                              </span>
                              <span class="btn btn-sm remove-contributor-icon gray" v-show="contributorIndex === 0" v-tooltip.hover.focus.click="{ ref: 'tooltipRefContributors', class: 'tooltip-whatisit' }">
                                <i class="fas fa-times"></i>
                              </span>
                              <span class="btn btn-sm remove-contributor-icon gray" v-show="contributorIndex === 1" v-tooltip.hover.focus.click="{ ref: 'tooltipRefProductionEngineering', class: 'tooltip-whatisit' }">
                                <i class="fas fa-times"></i>
                              </span>
                              <span class="btn btn-sm remove-contributor-icon gray" v-show="contributorIndex !== 0 && firstLyricist === contributorIndex" v-tooltip.hover.focus.click="{ ref: 'tooltipRefFeacturings', class: 'tooltip-whatisit' }">
                                <i class="fas fa-times"></i>
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="alert alert-warning mt-4" v-if="royaltiesAssignedPercentage !== 100">
                {{ $t('assigned_percentage_must') }}
              </div>
            </div>

            <!-- // Performers begin -->
            <div class="card-header header-track-section w-100 strong h4 card-body pl-4" v-show="visibleContributors[index]">
              <div class="row">
                <div class="text-center pt-3 col-12 col-md-6 col-sm-6 col-lg-5 text-lg-left text-md-left title-elemnt">
                  <span class="icon-card">
                    <i class="flaticon-user"></i>
                  </span>
                  {{ $t('performer') }}
                </div>
                <div class="text-center col-12 col-md-6 col-sm-6 col-lg-7 text-lg-right text-md-right">
                  <span class="more-info-link" v-html="$t('more_info_edit_distro')"></span>
                  <button type="button" class="btn btn-gray" @click="showPerfomersModal(index)" data-dismiss="modal">
                    <i class="fas fa-user mr-2"></i>{{ $t('add_new') }}
                  </button>
                  <span class="more-info-link mobile" v-html="$t('more_info_edit_distro')"></span>
                </div>
              </div>
            </div>

            <div class="card-body" v-show="visibleContributors[index]">
              <div class="row">
                <div class="col-md-12">
                  <div class="row" v-if="track.performers">
                    <div class="col-md-4 mt-2" v-for="(performer, performerIndex) in track.performers" v-bind:key="`performers_${performerIndex}`">
                      <div class="card mb-0 pos-relative already-spotify">
                        <div class="card-body pt-2 pb-2">
                          <div class="card-list p-0 mt-2">
                            <div class="item-list p-0">
                              <div class="info-user mt-2 ml-3">
                                <div class="h4 mb-0">
                                  <span class="title-artist pointer" @click="showPerfomersModal(index, performer, performerIndex)">
                                    <i class="fa fa-edit fa-2x mr-1"></i> {{ performer.name }}
                                  </span>
                                </div>
                                <div class="h5 text-muted followers m-0">
                                  <span>{{ $t(performer.instrument) }}</span>
                                </div>
                              </div>
                              <span class="btn btn-sm remove-contributor-icon gray" v-show="performerIndex === 0" v-tooltip.hover.focus.click="{ ref: 'tooltipRefPerformers', class: 'tooltip-whatisit' }">
                                <i class="fas fa-times"></i>
                              </span>
                              <span class="btn btn-sm remove-contributor-icon" @click="removePerformers(track, performerIndex, index)" v-if="(track.performers.length > 1)">
                                <i class="fas fa-times"></i>
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <!-- // Performers End-->
          </div>
        </div>
      </div>
      <div>
        <div class="text-right mb-4 mt-4">
          <button
            type="button"
            class="btn ml-1 mb-1"
            @click="addTrack()"
            v-if="(!distribution.release_type || distribution.release_type === 'album')"
          >
            {{ $t('add_track') }}
            <i class="fa fa-plus ml-1"></i>
          </button>
        </div>
        <hr>
        <div class="clearfix mt-4 mb-3">
          <div class="pull-right">
            <router-link
              tag="button"
              :to="`/distributions/${distribution._id}`"
              class="btn btn-border no-border"
              :disabled="uploading || saving"
            >
              <i class="fa fa-chevron-left mr-1"></i>
              {{ $t('back') }}
            </router-link>
            <button
              type="button"
              class="btn btn-gradient ml-1"
              @click="goToCover()"
              :disabled="uploading || saving"
            >
              <span v-if="!saving">
                {{ $t('continue') }}
                <i class="fa fa-chevron-right ml-1"></i>
              </span>
              <span v-if="saving">
                <i class="fa fa-spinner fa-spin"></i>
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <div ref="tooltipRefContributors" class="tooltip-content">
      <h4>{{ $t('main_composer') }}</h4>
      <p>{{ $t('main_composer_description') }}</p>
    </div>
    <div ref="tooltipRefFeacturings" class="tooltip-content">
      <h4>{{ $t('main_lyricist') }}</h4>
      <p>{{ $t('main_lyricist_description') }}</p>
    </div>
    <div ref="tooltipRefProductionEngineering" class="tooltip-content">
      <h4>{{ $t('main_production_engineering') }}</h4>
      <p>{{ $t('main_production_engineering_description') }}</p>
    </div>
    <div ref="tooltipRefPerformers" class="tooltip-content">
      <h4>{{ $t('main_performers') }}</h4>
      <p>{{ $t('main_performers_description') }}</p>
    </div>
    <div ref="limitedDistrosModal" class="modal" tabindex="-1" role="dialog" data-backdrop="static" data-keyboard="false">
      <div class="modal-dialog modal-md">
        <div class="modal-content">
          <div class="modal-body m-2">
            <div class="card card-pricing mb-0">
              <div class="card-header">
                <h4 class="strong">{{ $t('you_have_reached_contributors_limit') }}</h4>
                <div class="card-price mt-4">
                  <span class="bold h1">Indiefy</span>
                  <span class="h1 fw-light">PRO</span>
                </div>
              </div>
              <div class="card-body">
                <ul class="specification-list">
                  <li>
                    <span class="strong">{{ $t('premium_stores') }}</span>
                    <!-- <span class="status-specification muted">{{ $t('pro_releases_month') }}</span> -->
                  </li>
                  <li>
                    <span class="strong">{{ $t('number_of_projects') }}</span>
                    <span class="status-specification muted">{{ $t('pro_artists_or_bands') }}</span>
                  </li>
                  <li>
                    <span class="strong">{{ $t('number_of_tracks') }}</span>
                    <span class="status-specification muted">{{ $t('track_limit_pro') }}</span>
                  </li>
                  <li>
                    <span class="strong">{{ $t('deliveries') }}</span>
                    <span class="status-specification muted">{{ $t('express') }}</span>
                  </li>
                  <div class="text-left mt-2 small">
                    <router-link to="/upgrade?from=tracks_limit" class="strong">{{ $t('view_all_benefits') }}</router-link>
                  </div>
                </ul>
                <h1 class="text-center mt-4 text-primary strong my-5">
                  $<span class="price-big">4.16</span>/mo
                </h1>

                <div class="row">
                  <div class="col-md-6">
                    <button type="button" class="btn btn-gray w-100 mb-1" data-dismiss="modal">{{ $t('maybe_later') }}</button>
                  </div>
                  <div class="col-md-6">
                    <router-link tag="button" :to="`/upgrade?from=tracks_limit`" class="btn btn-primary w-100 mb-1">{{ $t('upgrade_now') }} <i class="fa fa-rocket ml-1"></i></router-link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div ref="limitedContributorsProModal" class="modal" tabindex="-1" role="dialog" data-backdrop="static" data-keyboard="false">
      <div class="modal-dialog modal-md">
        <div class="modal-content">
          <div class="modal-body text-center">
            <h3 class="strong mx-4 mt-3">{{ $t('you_have_reached_contributors_limit') }}</h3>
            <div class="my-5 mx-4">{{ $t('contributors_limit_pro_info') }}</div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-primary w-50" data-dismiss="modal">{{ $t('understood') }}</button>
          </div>
        </div>
      </div>
    </div>
    <div ref="limitedTracksModal" class="modal" tabindex="-1" role="dialog" data-backdrop="static" data-keyboard="false">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-body m-2">
            <div class="card card-pricing mb-0">
              <div class="card-header">
                <h4 class="strong">{{ $t('do_you_need_more') }}</h4>
                <div class="card-price mt-4">
                  <span class="bold h1">Indiefy</span>
                  <span class="h1 fw-light">PRO</span>
                </div>
              </div>
              <div class="card-body">
                <ul class="specification-list">
                  <li>
                    <span class="strong">{{ $t('premium_stores') }}</span>
                    <!-- <span class="status-specification muted">{{ $t('pro_releases_month') }}</span> -->
                  </li>
                  <li>
                    <span class="strong">{{ $t('number_of_projects') }}</span>
                    <span class="status-specification muted">{{ $t('pro_artists_or_bands') }}</span>
                  </li>
                  <li>
                    <span class="strong">{{ $t('number_of_tracks') }}</span>
                    <span class="status-specification muted">{{ $t('track_limit_pro') }}</span>
                  </li>
                  <li>
                    <span class="strong">{{ $t('deliveries') }}</span>
                    <span class="status-specification muted">{{ $t('express') }}</span>
                  </li>
                  <div class="text-left mt-2 small">
                    <router-link to="/upgrade?from=distribution-features" class="strong">{{ $t('view_all_benefits') }}</router-link>
                  </div>
                </ul>
                <h1 class="text-center mt-4 text-primary strong my-5">
                  $<span class="price-big">4.16</span>/mo
                </h1>

                <div class="row">
                  <div class="col-md-6">
                    <button type="button" class="btn btn-gray w-100 mb-1" data-dismiss="modal">{{ $t('maybe_later') }}</button>
                  </div>
                  <div class="col-md-6">
                    <router-link tag="button" :to="`/upgrade?from=distribution-features`" class="btn btn-primary w-100 mb-1">{{ $t('upgrade_now') }} <i class="fa fa-rocket ml-1"></i></router-link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div ref="modalContributors" class="modal modal-swal modalContributors" tabindex="-1" role="dialog" data-backdrop="static" data-keyboard="false">
      <div class="modal-dialog modal-center" role="document">
        <div class="modal-content">
          <div class="modal-header">
            {{ (!contributors.edit) ? $t('add_contributor') : $t('edit_contributor') }}
          </div>
          <div class="modal-body mb-5" v-if="modeContribuitor === 0">
            <div class="form-group">
              {{ $t('real_name_explanation') }}
            </div>
            <div class="form-group" :class="{ 'has-error': validForm }">
              <label for class="fw-bold">
                {{ $t('real_names') }}
                <span class="required-label">*</span>
              </label>
              <input
                type="text"
                class="form-control"
                required
                v-model="contributors.selectedName"
                maxlength="50"
                name="selectedName"
                @input="handleInput('selectedName')"
                @drop="$event.preventDefault()"
                @dragenter.prevent
                @dragover.prevent
                @change="validateContributorName(contributors)"
                @blur="validateName('selectedName', 'contributors')"
              />
            </div>
            <div class="form-group form-show-validation row" v-if="invalidNameReason">
              <div class="col-lg-12 col-md-12 col-sm-9">
                <div class="alert alert-warning">
                  {{ invalidNameReason }}
                </div>
              </div>
            </div>
            <div class="form-group" v-if="showMessageCover">
              <div class="alert alert-warning mt-2 padding-right-custom text-12 m-0" >
                {{ $t('message_cover') }}
              </div>
            </div>
            <div class="form-group" :class="{ 'has-error': validFormSelect }">
              <label for class="fw-bold">{{ $t('contributor_role') }}
              <span class="required-label">*</span></label>
              <Select2 v-model="contributors.selectedRole" :options="contributorsOptions" :disabled="contributors.edit"></Select2>
            </div>
            <div class="form-group" :class="{ 'has-error': validFormSelect }">
              <label for class="fw-bold">{{ $t('royalty_percentage') }} (Splits) <span class="badge badge-primary ml-1">PRO</span>
              </label>
              <input v-bind:readonly="!user.subscription" v-model="contributors.selectedPercentage" type="number" min="0" max="100" class="form-control">
              <p class="mt-2 pb-0 mb-0">
                {{ $t('splits_explanation') }}
              </p>
            </div>
            <div class="form-group" :class="{ 'has-error': contributorNotFound }" v-if="contributors.selectedPercentage > 0">
              <label for class="fw-bold">{{ $t('indiefy_user_email') }}
              </label>
              <input @keypress="invalidateUserEmail()" @change="validateUserEmail()" v-model="contributors.selectedContributorEmail" type="email" class="form-control">
              <p v-if="contributorNotFound" class="mt-2 pb-0 mb-0">
                {{ $t('no_indiefy_user') }}
              </p>
            </div>
          </div>
          <div class="modal-body mb-0" v-if="modeContribuitor === 1">
            <p class="text-center p-4" v-if="loadingSpotifyArtistsContribuitor">
              <i class="fa fa-spinner fa-spin text-muted"></i>
            </p>
            <p class="text-center p-4" v-if="!loadingSpotifyArtistsContribuitor && !spotifyArtistsContribuitor.length">
              <b>{{ contributors.selectedName }}</b> {{ $t('no_spotify_artists_found_feat') }}
            </p>
            <div class="row" v-if="!loadingSpotifyArtistsContribuitor && spotifyArtistsContribuitor.length">
              <div class="col-md-8 mt-1">
                <label for="">{{ $t('contributor_artist_spotify') }}</label>
              </div>
              <div class="col-md-4 text-right">
                <div @click="setCheckSpotifyArtistContribuitor()" class="display-inline">
                  <input type="checkbox" :id="`checkSpotifyArtistContribuitor`" data-toggle="toggle" data-onstyle="primary" data-style="btn-round" class="toggle-checkbox" data-size="small">
                </div>
              </div>
            </div>
            <div class="container-mask">
              <div class="mask" v-if="!checkSpotifyArtistContribuitor"></div>
              <div class="card-list">
                <div class="item-list" v-for="artist in spotifyArtistsContribuitor" v-bind:key="artist.id">
                  <div class="avatar-sm">
                    <img :src="artist.images[0] ? artist.images[0].url : defaultImage" :alt="artist.name" onerror="this.src = '/static/img/img-placeholder.jpg'" class="avatar-img rounded-circle">
                  </div>
                  <div class="info-user ml-3">
                    <a :href="artist.external_urls.spotify" target="_blank" class="username" v-if="artist.external_urls">
                      {{ artist.name }}
                      <i class="fas fa-external-link-alt"></i>
                    </a>
                    <span class="title-artist" v-if="!artist.external_urls">
                      {{ artist.name }}
                    </span>
                    <div class="status">{{ artist.followers.total }} {{ $t('followers') }}</div>
                  </div>
                  <button class="btn btn-primary btn-xs spotify-select" @click="addContributor(artist)">
                    {{ $t('select') }}
                  </button>
                </div>
              </div>
              <div class="spotify-paginator mt-4" v-if="spotifyArtistsContribuitor.length">
                <div class="paginator previous">
                  <button class="btn" @click="changeShowPotify(contributors, spotifyPrevContribuitor)" :disabled="!spotifyPrevContribuitor">
                    <i class="fas fa-angle-double-left"></i>
                    {{ $t('previous') }}
                  </button>
                </div>
                <div class="paginator next">
                  <button class="btn" @click="changeShowPotify(contributors, spotifyNextContribuitor)" :disabled="!spotifyNextContribuitor">
                    {{ $t('next') }}
                    <i class="fas fa-angle-double-right"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer" :class="modeContribuitor === 0 ? 'mt-2' : ''">
            <button type="button" class="btn btn-default btn-border no-border" data-dismiss="modal" @click="closeContributirModal()">{{ $t('close') }}</button>
            <button type="button" class="btn btn-primary" v-if="modeContribuitor === 0 || !checkSpotifyArtistContribuitor" @click="addContributor()" :disabled="contributorIndex !== 0 && checkingContributor || contributorNotFound || ((contributors.selectedContributorEmail !== '' && !isValidContributor) || (contributors.selectedPercentage > 0 && !contributors.selectedContributorEmail))">{{ (!contributors.edit) ? $t('add') : $t('edit') }}</button>
            <br><br>
            <!-- <button type="button" class="btn btn-primary" v-if="contributors.selectedRole === 'Remixer' && modeContribuitor === 0" @click="changeShowPotify(contributors)">{{ $t('find_in_spotify') }}</button> -->
            <div v-if="!contributors.edit && modeContribuitor === 0">
              <div class="col-md-12 custom-control custom-checkbox mt-3">
                <input
                  v-model="addOther"
                  type="checkbox"
                  class="custom-control-input"
                  name="addOther"
                  id="addOther"
                />
                <label class="custom-control-label label-break-spaces" for="addOther" v-html="$t('add_another_contributor')">
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div ref="modalPerformers" class="modal modal-swal modalContributors" tabindex="-1" role="dialog" data-backdrop="static" data-keyboard="false">
      <div class="modal-dialog modal-center" role="document">
        <div class="modal-content">
          <div class="modal-header">
            {{ (!performers.edit) ? $t('add_performer') : $t('edit_performer') }}
          </div>
          <div class="modal-body mb-5">
            <div class="form-group">
              {{ $t('real_name_performers_explanation') }}
            </div>
            <div class="form-group" :class="{ 'has-error': validForm }">
              <label for class="fw-bold">
                {{ $t('real_names') }}
                <span class="required-label">*</span>
              </label>
              <input
                type="text"
                class="form-control"
                required
                v-model="performers.selectedName"
                maxlength="50"
                name="selectedNamePerformers"
                @input="handleInput('selectedNamePerformers')"
                @drop="$event.preventDefault()" @dragenter.prevent @dragover.prevent
                @change="validatePerformerName(performers)"
                @paste="$event.preventDefault()"
                @blur="validateName('selectedNamePerformers', 'performers')"
              />
            </div>
            <div class="form-group form-show-validation row" v-if="invalidNamePerformerReason">
              <div class="col-lg-12 col-md-12 col-sm-9">
                <div class="alert alert-warning">
                  {{ invalidNamePerformerReason }}
                </div>
              </div>
            </div>
            <div class="form-group" v-if="showMessageCover">
              <div class="alert alert-warning mt-2 padding-right-custom text-12 m-0" >
                {{ $t('message_cover') }}
              </div>
            </div>
            <div class="form-group" :class="{ 'has-error': validFormSelect }">
              <label for class="fw-bold">{{ $t('instrument') }}
              <span class="required-label">*</span></label>
              <Select2 v-model="performers.selectedRole" :options="performersOptions" :disabled="performers.edit"></Select2>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-default btn-border no-border" data-dismiss="modal" @click="closePerformersModal()">{{ $t('close') }}</button>
            <button type="button" class="btn btn-primary" @click="addPerformers()">{{ (!performers.edit) ? $t('add') : $t('edit') }}</button>
            <br><br>
            <div v-if="!performers.edit">
              <div class="col-md-12 custom-control custom-checkbox mt-3">
                <input
                  v-model="addOther"
                  type="checkbox"
                  class="custom-control-input"
                  name="addOther"
                  id="addOther"
                />
                <label class="custom-control-label label-break-spaces" for="addOther" v-html="$t('add_another_performer')">
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div ref="modalFeaturings" class="modal modal-swal modalFeaturings" tabindex="-1" role="dialog" data-backdrop="static" data-keyboard="false">
      <div class="modal-dialog modal-center" role="document">
        <div class="modal-content">
          <div class="modal-header">
            {{ $t('add_featuring_artist') }}
          </div>
          <div class="modal-body mb-4" v-if="featStep === 0">
            <div class="form-group" :class="{ 'has-error': validForm }">
              <label for>
                {{ $t('name') }}
                <span class="required-label">*</span>
              </label>
              <div class="input-group">
                <div class="input-group-prepend">
                  <span class="input-group-text" id="username-addon" title="Featured Artists">Ft.</span>
                </div>
                <input
                  type="text"
                  class="form-control"
                  required
                  ref="featName"
                  name="featNameInput"
                  v-model="featuring.name"
                  maxlength="50"
                  @drop="$event.preventDefault()" @dragenter.prevent @dragover.prevent
                  @paste="$event.preventDefault()"
                  @input="handleInput('featNameInput')"
                  @change="validateFeaturingName(featuring)"
                />
              </div>
            </div>
            <div class="row pd-row mt-4">
              <div class="form-group col-md-8 what-is">
                <label for="">{{ $t('featuring_artist_primary') }}</label>
                <span class="tooltip-label float-left pl-0" v-tooltip.hover.focus.click="{ ref: 'tooltipRefFeacturing', class: 'tooltip-whatisit' }">
                  {{ $t('what_this') }}
                </span>
              </div>
              <div class="col-md-4 mt-2 text-right check-box-primary">
                <div @click="setPrimaryArtist()" class="display-inline">
                  <input type="checkbox" :id="`checkPrimaryArtist`" data-toggle="toggle" data-onstyle="primary" data-style="btn-round" class="toggle-checkbox" data-size="small">
                </div>
              </div>
            </div>
          </div>
          <div class="modal-body pr-5 pl-5" v-if="featStep === 1">
            <p class="text-center p-4" v-if="loadingSpotifyArtists">
              <i class="fa fa-spinner fa-spin text-muted"></i>
            </p>
            <p class="text-center p-4" v-if="!loadingSpotifyArtists && !spotifyArtists.length">
              <b>{{ featuringName }}</b> {{ $t('no_spotify_artists_found_feat') }}
            </p>
            <div class="row" v-if="!loadingSpotifyArtists && spotifyArtists.length">
              <div class="col-md-8 mt-1">
                <label for="">{{ $t('featuring_artist_spotify') }}</label>
              </div>
              <div class="col-md-4 text-right">
                <div @click="setCheckSpotifyArtist()" class="display-inline">
                  <input type="checkbox" :id="`checkSpotifyArtist`" data-toggle="toggle" data-onstyle="primary" data-style="btn-round" class="toggle-checkbox" data-size="small">
                </div>
              </div>
            </div>
            <div class="container-mask">
              <div class="mask" v-if="!checkSpotifyArtist"></div>
              <div class="card-list">
                <div class="item-list" v-for="artist in spotifyArtists" v-bind:key="artist.id">
                  <div class="avatar-sm">
                    <img :src="artist.images[0] ? artist.images[0].url : defaultImage" :alt="artist.name" onerror="this.src = '/static/img/img-placeholder.jpg'" class="avatar-img rounded-circle">
                  </div>
                  <div class="info-user ml-3">
                    <a :href="artist.external_urls.spotify" target="_blank" class="username" v-if="artist.external_urls">
                      {{ artist.name }}
                      <i class="fas fa-external-link-alt"></i>
                    </a>
                    <span class="title-artist" v-if="!artist.external_urls">
                      {{ artist.name }}
                    </span>
                    <div class="status">{{ artist.followers.total }} {{ $t('followers') }}</div>
                  </div>
                  <button class="btn btn-primary btn-xs spotify-select" @click="selectSpotifyArtist(artist)">
                    {{ $t('select') }}
                  </button>
                </div>
              </div>
              <div class="spotify-paginator mt-4" v-if="spotifyArtists.length">
                <div class="paginator previous">
                  <button class="btn" @click="findSpotifyArtist(spotifyPrev)" :disabled="!spotifyPrev">
                    <i class="fas fa-angle-double-left"></i>
                    {{ $t('previous') }}
                  </button>
                </div>
                <div class="paginator next">
                  <button class="btn" @click="findSpotifyArtist(spotifyNext)" :disabled="!spotifyNext">
                    {{ $t('next') }}
                    <i class="fas fa-angle-double-right"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer mt-1">
            <button type="button" class="btn btn-default btn-border no-border" data-dismiss="modal" @click="closeFeaturingModal()">{{ $t('close') }}</button>
            <button type="button" class="btn btn-primary" v-if="featStep === 0" @click="findSpotifyArtist()">{{ $t('next') }}</button>
            <button type="button" class="btn btn-primary" v-if="featStep === 1 && !checkSpotifyArtist" @click="addFeaturingNoSpotify()">{{ $t('add') }}</button>
          </div>
          <div class="col-md-12 custom-control pb-5 pt-0 modal-footer" v-show="featStep === 1">
            <input v-model="addOther" type="checkbox" class="custom-control-input" name="addOtherArtist" id="addOtherArtist" />
            <label class="custom-control-label label-break-spaces" for="addOtherArtist" v-html="$t('add_another_featuring')">
            </label>
          </div>
          <div ref="tooltipRefFeacturing" class="tooltip-content" v-if="featStep === 0">
            <h4>{{ $t('primary_artist') }}</h4>
            <p>{{ $t('primary_artist_description') }}</p>
          </div>
        </div>
      </div>
    </div>
    <div ref="modalSwitchToAlbum" class="modal modal-swal modalFeaturings" tabindex="-1" role="dialog" data-backdrop="static" data-keyboard="false">
      <div class="modal-dialog modal-lg" role="document">
        <div class="modal-content">
          <div class="modal-header pt-4 pb-4">
            <h4 class="modal-title">{{ $t('attention') }}</h4>
          </div>
          <div class="modal-body pr-5 pl-5">
            <p class="text-center p-4 mb-0">
              <b>{{ $t('you_need_add_more_tracks') }}</b>
            </p>
            <p class="text-center">{{ $t('switch_to_single') }}</p>
          </div>
          <div class="modal-footer text-center pt-5">
            <button class="btn mr-2 btn-default btn-border btn-cancel no-border" data-dismiss="modal">{{ $t('cancel') }}</button>
            <button type="button" class="btn btn-primary" @click="switchToAlbum()">
              <span v-if="!loading">{{ $t('yes') }}</span>
              <span v-if="loading"><i class="fa fa-spinner fa-spin"></i></span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import Vue from 'vue'
import _ from 'underscore'
import Distributions from '../services/distributions'
import Artists from '../services/artists'
import Select2 from './utils/Select2'
import Upload from '../services/upload'
import genres from '../data/genres'
import performanceLanguages from '../data/performance-languages'
import filters from './utils/filters'
import contributorsRoles from '../data/contributors-roles'
import performersRoles from '../data/performers-instruments'
import User from '../services/user'
import DistributionProgress from './utils/DistributionProgress'
import Configuration from '../services/configuration'
import Public from '../services/public'
import LANGUAGE_REGEX from '../data/productlanguagesregex'

const songBase = {
  title: '',
  main_genre: '',
  style: '',
  version: '',
  explicit: 0,
  producer: '',
  composer: '',
  performer: '',
  songwriter: '',
  featuring: null,
  isrc: '',
  file_url: '',
  hasLyrics: false,
  performance_language: 'ZXX',
  contributors: {
    showInput: false,
    selectedName: '',
    selectedRole: '',
    selectedContributorEmail: '',
    selectedPercentage: 0,
    data: []
  }
}

const defaultDiacriticsRemovalMap = [
  {'base': 'A', 'letters': /[\u0041\u24B6\uFF21\u00C0\u00C1\u00C2\u1EA6\u1EA4\u1EAA\u1EA8\u00C3\u0100\u0102\u1EB0\u1EAE\u1EB4\u1EB2\u0226\u01E0\u00C4\u01DE\u1EA2\u00C5\u01FA\u01CD\u0200\u0202\u1EA0\u1EAC\u1EB6\u1E00\u0104\u023A\u2C6F]/g},
  {'base': 'AA', 'letters': /[\uA732]/g},
  {'base': 'AE', 'letters': /[\u00C6\u01FC\u01E2]/g},
  {'base': 'AO', 'letters': /[\uA734]/g},
  {'base': 'AU', 'letters': /[\uA736]/g},
  {'base': 'AV', 'letters': /[\uA738\uA73A]/g},
  {'base': 'AY', 'letters': /[\uA73C]/g},
  {'base': 'B', 'letters': /[\u0042\u24B7\uFF22\u1E02\u1E04\u1E06\u0243\u0182\u0181]/g},
  {'base': 'C', 'letters': /[\u0043\u24B8\uFF23\u0106\u0108\u010A\u010C\u00C7\u1E08\u0187\u023B\uA73E]/g},
  {'base': 'D', 'letters': /[\u0044\u24B9\uFF24\u1E0A\u010E\u1E0C\u1E10\u1E12\u1E0E\u0110\u018B\u018A\u0189\uA779]/g},
  {'base': 'DZ', 'letters': /[\u01F1\u01C4]/g},
  {'base': 'Dz', 'letters': /[\u01F2\u01C5]/g},
  {'base': 'E', 'letters': /[\u0045\u24BA\uFF25\u00C8\u00C9\u00CA\u1EC0\u1EBE\u1EC4\u1EC2\u1EBC\u0112\u1E14\u1E16\u0114\u0116\u00CB\u1EBA\u011A\u0204\u0206\u1EB8\u1EC6\u0228\u1E1C\u0118\u1E18\u1E1A\u0190\u018E]/g},
  {'base': 'F', 'letters': /[\u0046\u24BB\uFF26\u1E1E\u0191\uA77B]/g},
  {'base': 'G', 'letters': /[\u0047\u24BC\uFF27\u01F4\u011C\u1E20\u011E\u0120\u01E6\u0122\u01E4\u0193\uA7A0\uA77D\uA77E]/g},
  {'base': 'H', 'letters': /[\u0048\u24BD\uFF28\u0124\u1E22\u1E26\u021E\u1E24\u1E28\u1E2A\u0126\u2C67\u2C75\uA78D]/g},
  {'base': 'I', 'letters': /[\u0049\u24BE\uFF29\u00CC\u00CD\u00CE\u0128\u012A\u012C\u0130\u00CF\u1E2E\u1EC8\u01CF\u0208\u020A\u1ECA\u012E\u1E2C\u0197]/g},
  {'base': 'J', 'letters': /[\u004A\u24BF\uFF2A\u0134\u0248]/g},
  {'base': 'K', 'letters': /[\u004B\u24C0\uFF2B\u1E30\u01E8\u1E32\u0136\u1E34\u0198\u2C69\uA740\uA742\uA744\uA7A2]/g},
  {'base': 'L', 'letters': /[\u004C\u24C1\uFF2C\u013F\u0139\u013D\u1E36\u1E38\u013B\u1E3C\u1E3A\u0141\u023D\u2C62\u2C60\uA748\uA746\uA780]/g},
  {'base': 'LJ', 'letters': /[\u01C7]/g},
  {'base': 'Lj', 'letters': /[\u01C8]/g},
  {'base': 'M', 'letters': /[\u004D\u24C2\uFF2D\u1E3E\u1E40\u1E42\u2C6E\u019C]/g},
  {'base': 'N', 'letters': /[\u004E\u24C3\uFF2E\u01F8\u0143\u00D1\u1E44\u0147\u1E46\u0145\u1E4A\u1E48\u0220\u019D\uA790\uA7A4]/g},
  {'base': 'NJ', 'letters': /[\u01CA]/g},
  {'base': 'Nj', 'letters': /[\u01CB]/g},
  {'base': 'O', 'letters': /[\u004F\u24C4\uFF2F\u00D2\u00D3\u00D4\u1ED2\u1ED0\u1ED6\u1ED4\u00D5\u1E4C\u022C\u1E4E\u014C\u1E50\u1E52\u014E\u022E\u0230\u00D6\u022A\u1ECE\u0150\u01D1\u020C\u020E\u01A0\u1EDC\u1EDA\u1EE0\u1EDE\u1EE2\u1ECC\u1ED8\u01EA\u01EC\u00D8\u01FE\u0186\u019F\uA74A\uA74C]/g},
  {'base': 'OI', 'letters': /[\u01A2]/g},
  {'base': 'OO', 'letters': /[\uA74E]/g},
  {'base': 'OU', 'letters': /[\u0222]/g},
  {'base': 'P', 'letters': /[\u0050\u24C5\uFF30\u1E54\u1E56\u01A4\u2C63\uA750\uA752\uA754]/g},
  {'base': 'Q', 'letters': /[\u0051\u24C6\uFF31\uA756\uA758\u024A]/g},
  {'base': 'R', 'letters': /[\u0052\u24C7\uFF32\u0154\u1E58\u0158\u0210\u0212\u1E5A\u1E5C\u0156\u1E5E\u024C\u2C64\uA75A\uA7A6\uA782]/g},
  {'base': 'S', 'letters': /[\u0053\u24C8\uFF33\u1E9E\u015A\u1E64\u015C\u1E60\u0160\u1E66\u1E62\u1E68\u0218\u015E\u2C7E\uA7A8\uA784]/g},
  {'base': 'T', 'letters': /[\u0054\u24C9\uFF34\u1E6A\u0164\u1E6C\u021A\u0162\u1E70\u1E6E\u0166\u01AC\u01AE\u023E\uA786]/g},
  {'base': 'TZ', 'letters': /[\uA728]/g},
  {'base': 'U', 'letters': /[\u0055\u24CA\uFF35\u00D9\u00DA\u00DB\u0168\u1E78\u016A\u1E7A\u016C\u00DC\u01DB\u01D7\u01D5\u01D9\u1EE6\u016E\u0170\u01D3\u0214\u0216\u01AF\u1EEA\u1EE8\u1EEE\u1EEC\u1EF0\u1EE4\u1E72\u0172\u1E76\u1E74\u0244]/g},
  {'base': 'V', 'letters': /[\u0056\u24CB\uFF36\u1E7C\u1E7E\u01B2\uA75E\u0245]/g},
  {'base': 'VY', 'letters': /[\uA760]/g},
  {'base': 'W', 'letters': /[\u0057\u24CC\uFF37\u1E80\u1E82\u0174\u1E86\u1E84\u1E88\u2C72]/g},
  {'base': 'X', 'letters': /[\u0058\u24CD\uFF38\u1E8A\u1E8C]/g},
  {'base': 'Y', 'letters': /[\u0059\u24CE\uFF39\u1EF2\u00DD\u0176\u1EF8\u0232\u1E8E\u0178\u1EF6\u1EF4\u01B3\u024E\u1EFE]/g},
  {'base': 'Z', 'letters': /[\u005A\u24CF\uFF3A\u0179\u1E90\u017B\u017D\u1E92\u1E94\u01B5\u0224\u2C7F\u2C6B\uA762]/g},
  {'base': 'a', 'letters': /[\u0061\u24D0\uFF41\u1E9A\u00E0\u00E1\u00E2\u1EA7\u1EA5\u1EAB\u1EA9\u00E3\u0101\u0103\u1EB1\u1EAF\u1EB5\u1EB3\u0227\u01E1\u00E4\u01DF\u1EA3\u00E5\u01FB\u01CE\u0201\u0203\u1EA1\u1EAD\u1EB7\u1E01\u0105\u2C65\u0250]/g},
  {'base': 'aa', 'letters': /[\uA733]/g},
  {'base': 'ae', 'letters': /[\u00E6\u01FD\u01E3]/g},
  {'base': 'ao', 'letters': /[\uA735]/g},
  {'base': 'au', 'letters': /[\uA737]/g},
  {'base': 'av', 'letters': /[\uA739\uA73B]/g},
  {'base': 'ay', 'letters': /[\uA73D]/g},
  {'base': 'b', 'letters': /[\u0062\u24D1\uFF42\u1E03\u1E05\u1E07\u0180\u0183\u0253]/g},
  {'base': 'c', 'letters': /[\u0063\u24D2\uFF43\u0107\u0109\u010B\u010D\u00E7\u1E09\u0188\u023C\uA73F\u2184]/g},
  {'base': 'd', 'letters': /[\u0064\u24D3\uFF44\u1E0B\u010F\u1E0D\u1E11\u1E13\u1E0F\u0111\u018C\u0256\u0257\uA77A]/g},
  {'base': 'dz', 'letters': /[\u01F3\u01C6]/g},
  {'base': 'e', 'letters': /[\u0065\u24D4\uFF45\u00E8\u00E9\u00EA\u1EC1\u1EBF\u1EC5\u1EC3\u1EBD\u0113\u1E15\u1E17\u0115\u0117\u00EB\u1EBB\u011B\u0205\u0207\u1EB9\u1EC7\u0229\u1E1D\u0119\u1E19\u1E1B\u0247\u025B\u01DD]/g},
  {'base': 'f', 'letters': /[\u0066\u24D5\uFF46\u1E1F\u0192\uA77C]/g},
  {'base': 'g', 'letters': /[\u0067\u24D6\uFF47\u01F5\u011D\u1E21\u011F\u0121\u01E7\u0123\u01E5\u0260\uA7A1\u1D79\uA77F]/g},
  {'base': 'h', 'letters': /[\u0068\u24D7\uFF48\u0125\u1E23\u1E27\u021F\u1E25\u1E29\u1E2B\u1E96\u0127\u2C68\u2C76\u0265]/g},
  {'base': 'hv', 'letters': /[\u0195]/g},
  {'base': 'i', 'letters': /[\u0069\u24D8\uFF49\u00EC\u00ED\u00EE\u0129\u012B\u012D\u00EF\u1E2F\u1EC9\u01D0\u0209\u020B\u1ECB\u012F\u1E2D\u0268\u0131]/g},
  {'base': 'j', 'letters': /[\u006A\u24D9\uFF4A\u0135\u01F0\u0249]/g},
  {'base': 'k', 'letters': /[\u006B\u24DA\uFF4B\u1E31\u01E9\u1E33\u0137\u1E35\u0199\u2C6A\uA741\uA743\uA745\uA7A3]/g},
  {'base': 'l', 'letters': /[\u006C\u24DB\uFF4C\u0140\u013A\u013E\u1E37\u1E39\u013C\u1E3D\u1E3B\u017F\u0142\u019A\u026B\u2C61\uA749\uA781\uA747]/g},
  {'base': 'lj', 'letters': /[\u01C9]/g},
  {'base': 'm', 'letters': /[\u006D\u24DC\uFF4D\u1E3F\u1E41\u1E43\u0271\u026F]/g},
  {'base': 'n', 'letters': /[\u006E\u24DD\uFF4E\u01F9\u0144\u00F1\u1E45\u0148\u1E47\u0146\u1E4B\u1E49\u019E\u0272\u0149\uA791\uA7A5]/g},
  {'base': 'nj', 'letters': /[\u01CC]/g},
  {'base': 'o', 'letters': /[\u006F\u24DE\uFF4F\u00F2\u00F3\u00F4\u1ED3\u1ED1\u1ED7\u1ED5\u00F5\u1E4D\u022D\u1E4F\u014D\u1E51\u1E53\u014F\u022F\u0231\u00F6\u022B\u1ECF\u0151\u01D2\u020D\u020F\u01A1\u1EDD\u1EDB\u1EE1\u1EDF\u1EE3\u1ECD\u1ED9\u01EB\u01ED\u00F8\u01FF\u0254\uA74B\uA74D\u0275]/g},
  {'base': 'oi', 'letters': /[\u01A3]/g},
  {'base': 'ou', 'letters': /[\u0223]/g},
  {'base': 'oo', 'letters': /[\uA74F]/g},
  {'base': 'p', 'letters': /[\u0070\u24DF\uFF50\u1E55\u1E57\u01A5\u1D7D\uA751\uA753\uA755]/g},
  {'base': 'q', 'letters': /[\u0071\u24E0\uFF51\u024B\uA757\uA759]/g},
  {'base': 'r', 'letters': /[\u0072\u24E1\uFF52\u0155\u1E59\u0159\u0211\u0213\u1E5B\u1E5D\u0157\u1E5F\u024D\u027D\uA75B\uA7A7\uA783]/g},
  {'base': 's', 'letters': /[\u0073\u24E2\uFF53\u00DF\u015B\u1E65\u015D\u1E61\u0161\u1E67\u1E63\u1E69\u0219\u015F\u023F\uA7A9\uA785\u1E9B]/g},
  {'base': 't', 'letters': /[\u0074\u24E3\uFF54\u1E6B\u1E97\u0165\u1E6D\u021B\u0163\u1E71\u1E6F\u0167\u01AD\u0288\u2C66\uA787]/g},
  {'base': 'tz', 'letters': /[\uA729]/g},
  {'base': 'u', 'letters': /[\u0075\u24E4\uFF55\u00F9\u00FA\u00FB\u0169\u1E79\u016B\u1E7B\u016D\u00FC\u01DC\u01D8\u01D6\u01DA\u1EE7\u016F\u0171\u01D4\u0215\u0217\u01B0\u1EEB\u1EE9\u1EEF\u1EED\u1EF1\u1EE5\u1E73\u0173\u1E77\u1E75\u0289]/g},
  {'base': 'v', 'letters': /[\u0076\u24E5\uFF56\u1E7D\u1E7F\u028B\uA75F\u028C]/g},
  {'base': 'vy', 'letters': /[\uA761]/g},
  {'base': 'w', 'letters': /[\u0077\u24E6\uFF57\u1E81\u1E83\u0175\u1E87\u1E85\u1E98\u1E89\u2C73]/g},
  {'base': 'x', 'letters': /[\u0078\u24E7\uFF58\u1E8B\u1E8D]/g},
  {'base': 'y', 'letters': /[\u0079\u24E8\uFF59\u1EF3\u00FD\u0177\u1EF9\u0233\u1E8F\u00FF\u1EF7\u1E99\u1EF5\u01B4\u024F\u1EFF]/g},
  {'base': 'z', 'letters': /[\u007A\u24E9\uFF5A\u017A\u1E91\u017C\u017E\u1E93\u1E95\u01B6\u0225\u0240\u2C6C\uA763]/g}
]

export default {
  name: 'DistributionSongs',
  data: () => {
    return {
      invalidNameReason: '',
      invalidNamePerformerReason: '',
      isValidContributor: false,
      royaltiesAssignedPercentage: 100,
      previewing: false,
      name: 'DistributionSongs',
      explicitAlertShown: false,
      saving: false,
      changes: defaultDiacriticsRemovalMap,
      addOther: false,
      user: {},
      validForm: false,
      validFormSelect: false,
      contributorNotFound: false,
      checkingContributor: false,
      countLyricist: 0,
      firstLyricist: null,
      isPro: true,
      versions: [
        { id: '', text: 'Original' },
        { id: 'Cover', text: 'Cover' },
        { id: 'Live Version', text: 'Live Version' },
        { id: 'Radio Edit', text: 'Radio Edit' },
        { id: 'Instrumental Version', text: 'Instrumental' },
        { id: 'Extended Version', text: 'Extended Version' },
        { id: 'Remix', text: 'Remix' }
      ],
      featured: {},
      contributors: {},
      performers: {},
      performanceLanguages: _.map(performanceLanguages, item => {
        return { id: item.value, text: item.name }
      }),
      artists: [],
      genresSource: genres,
      genres: _.map(genres, (genre, key) => {
        return key
      }),
      distribution: {
        _id: '',
        main_genre: '',
        songs: [JSON.parse(JSON.stringify(songBase))]
      },
      song: null,
      songIndex: 0,
      contributorIndex: null,
      emptyData: true,
      lang: '',
      addContributorLang: '',
      contributorsOptions: [],
      performersOptions: [],
      trackSectionFlags: [false],
      trackSectionFlags2: [false],
      featuring: {
        name: '',
        primaryArtist: 0,
        spotify_id: null,
        details: {
          followers: '',
          img: '',
          external_urls: ''
        }
      },
      metadataSongs: [],
      composer: 'Composer',
      productionEngineering: 'Producer',
      performerInstrument: 'keyboards',
      lyricist: 'Lyricist',
      featStep: 0,
      spotifyProfile: [],
      spotifyArtists: [],
      loadingSpotifyArtists: false,
      checkSpotifyArtist: false,
      featuringName: '',
      defaultImage: '/static/img/spotify-logo-big.png',
      defaultImageBlack: '/static/img/indiefy-logo-white-stroke.png',
      modeContribuitor: 0,
      loadingSpotifyArtistsContribuitor: false,
      checkSpotifyArtistContribuitor: false,
      spotifyArtistsContribuitor: [],
      reopeningOptions: [],
      loading: false,
      metadata_request_data: {},
      distributionOriginal: {},
      metadataChangeClass: {},
      spotifyPrev: null,
      spotifyNext: null,
      spotifyPrevContribuitor: null,
      spotifyNextContribuitor: null,
      enableMetadata: false,
      loadingMetadata: false,
      showMessageCover: false,
      freeTracksLimit: 3,
      wavesurfer: {},
      visibleContributors: {}
    }
  },
  computed: {
    uploading: function () {
      return _.find(this.distribution.songs, song => song.uploading)
    },
    setSongStyle: function (a, b) {
      return this.distribution.songs.map(song => {
        return song.main_genre
      })
    }
  },
  methods: {
    toggleContributorsView: function (index) {
      this.visibleContributors[index] = !this.visibleContributors[index]
      this.$forceUpdate()
    },
    validateName: async function (component, model) {
      const selectedName = this[model] && this[model].selectedName ? this[model].selectedName.trim() : null

      if (!selectedName) {
        return
      }

      let invalidReasonKey = ''
      if (component === 'selectedName') {
        invalidReasonKey = 'invalidNameReason'
      } else if (component === 'selectedNamePerformers') {
        invalidReasonKey = 'invalidNamePerformerReason'
      }

      if (invalidReasonKey) {
        this[invalidReasonKey] = ''

        try {
          const response = await User.validateLegalName(selectedName)
          if (!response.data.valid && response.data.reason) {
            this[invalidReasonKey] = response.data.reason
          }
        } catch (err) {
          console.log('Error when trying to request name validation', err)
        }
      }
    },
    invalidateUserEmail: function () {
      this.checkingContributor = true
      this.isValidContributor = false
    },
    validateUserEmail: _.debounce(function () {
      if (!this.contributors.selectedContributorEmail) return

      this.checkingContributor = true
      this.contributorNotFound = false
      this.isValidContributor = false

      User.validateUserEmail(this.contributors.selectedContributorEmail).then(response => {
        this.checkingContributor = false
        this.isValidContributor = true
      }).catch(() => {
        this.checkingContributor = false
        this.contributorNotFound = true
        this.isValidContributor = false
      })
    }, 1000),
    playPreview: function (index) {
      if (this.wavesurfer[index].playing) {
        this.wavesurfer[index].playing = false
        this.wavesurfer[index].pause()
      } else {
        this.wavesurfer[index].playing = true
        this.wavesurfer[index].regions.list['main'].playLoop()
      }

      this.$forceUpdate()
    },
    initWaveform: function (songIndex) {
      if (!this.user.subscription) {
        return window.$(this.$refs.limitedTracksModal).modal('show')
      }

      let song = this.distribution.songs[songIndex]

      if (song.custom_preview) return

      this.wavesurfer[songIndex] = null
      song.custom_preview = true

      setTimeout(() => {
        if (song.file_url && song.duration && this.$refs[`wavesurfer_${songIndex}`][0]) {
          this.wavesurfer[songIndex] = window.WaveSurfer.create({
            interact: false,
            container: this.$refs[`wavesurfer_${songIndex}`][0],
            waveColor: 'lightblue',
            progressColor: '#007bff',
            plugins: [
              window.WaveSurfer.regions.create({
                regions: [
                  {
                    id: 'main',
                    minLength: 15,
                    maxLength: 60,
                    start: song.preview_start || 0,
                    end: song.preview_end || 30,
                    loop: false,
                    color: 'hsla(227, 100%, 50%, 0.1)'
                  }
                ]
              })
            ]
          })

          this.wavesurfer[songIndex].load(song.file_url)

          this.wavesurfer[songIndex].on('ready', () => {
            song.preview_start = this.wavesurfer[songIndex].regions.list['main'].start
            song.preview_end = this.wavesurfer[songIndex].regions.list['main'].end

            if (this.wavesurfer[songIndex]) {
              this.wavesurfer[songIndex].loaded = true
              this.$forceUpdate()
            }
          })

          this.wavesurfer[songIndex].regions.list['main'].on('play', () => {
            this.wavesurfer[songIndex].playing = true
          })

          this.wavesurfer[songIndex].regions.list['main'].on('pause', () => {
            this.wavesurfer[songIndex].playing = false
          })

          this.wavesurfer[songIndex].on('region-update-end', () => {
            song.preview_start = parseInt(this.wavesurfer[songIndex].regions.list['main'].start)
            song.preview_end = parseInt(this.wavesurfer[songIndex].regions.list['main'].end)

            if (this.wavesurfer[songIndex].playing) {
              this.playPreview(songIndex)

              setTimeout(() => {
                this.playPreview(songIndex)
              }, 1)
            } else {
              this.$forceUpdate()
            }
          })
        } else {
          this.initWaveform(songIndex)
        }
      }, 500)

      this.$forceUpdate()
    },
    getQuery: function (metadata) {
      let query = ''

      if (metadata.Title) {
        query += metadata.Title.replace(/s/g, '+') + '+'
      }

      if (metadata.Artist) {
        query += metadata.Artist.replace(/s/g, '+')
      }

      return query
    },
    removeDiacritics: function (str) {
      _.map(this.changes, (item) => {
        str = str.replace(item.letters, item.base)
      })
      return str.normalize('NFD').replace(/[^a-zA-Z0-9]/g, '').toLowerCase()
    },
    setLyrics: function (index) {
      this.countLyricist = 0
      _.map(this.distribution.songs[index].contributors.data, (item, key) => {
        this.countLyricist += (item.role === 'Lyricist') ? 1 : 0
      })

      if (this.countLyricist === 0) {
        let performanceLanguage = (window.$('input[name=checkLyrics' + index + ']').prop('checked')) ? 'EN' : 'ZXX'
        this.distribution.songs[index].hasLyrics = (window.$('input[name=checkLyrics' + index + ']').prop('checked'))
        Vue.set(this.distribution.songs[index], 'performance_language', performanceLanguage)
        this.distribution.songs[index].performance_language = performanceLanguage
      } else {
        this.distribution.songs[index].hasLyrics = false
      }
      let data = this.distribution.songs[index].contributors.data || []

      if (window.$('input[name=checkLyrics' + index + ']').prop('checked')) {
        if (!_.find(data, contributor => contributor.role === this.lyricist)) {
          this.setDefaultContributor(this.distribution.songs[index], this.lyricist)
        }
      } else {
        if (this.countLyricist > 0) {
          this.distribution.songs[index].contributors.data = _.filter(data, (contributor, contributorIndex) => contributor.role !== this.lyricist)
        }
      }

      this.firstLyricist = null
      _.map(this.distribution.songs[index].contributors.data, (item, key) => {
        if (!this.firstLyricist && item.role === 'Lyricist') {
          this.firstLyricist = key
        }
      })

      this.$forceUpdate()
    },
    replaceAudio: function (track, index) {
      this.songIndex = index

      new Promise(resolve => {
        if (track.file_url) {
          Upload.removeSong('/distribution-songs', track.file_url.split('/').pop()).then(() => {
            resolve()
          })
        } else {
          resolve()
        }
      }).then(() => {
        track.file_name = ''
        track.file_size = ''
        track.file_url = ''
        track.duration = ''
        track.preview_start = 0
        track.preview_end = 30

        this.saveDistribution()
        this.$forceUpdate()
      })
    },
    setExplicit: function (track, index) {
      Vue.set(track, 'explicit', !window.$('#checkExplicit' + index).prop('checked'))
      this.$forceUpdate()

      if (!window.$('#checkExplicit' + index).prop('checked')) {
        this.explicitAlertShown = true

        let html = document.createElement('div')
        html.innerHTML = `${this.$t('non_explicit_explanation')}`

        window.swal({
          title: this.$t('are_you_sure'),
          content: html,
          closeOnEsc: false,
          closeOnClickOutside: false,
          buttons: {
            cancel: {
              text: this.$t('cancel'),
              visible: true,
              className: 'btn btn-default btn-border no-border'
            },
            confirm: {
              text: this.$t('is_explicit_ok'),
              className: 'btn btn-primary'
            }
          }
        }).then(confirm => {
          if (!confirm) {
            Vue.set(track, 'explicit', false)
            window.$(`#checkExplicit${index}`).prop('checked', false).change()
            this.$forceUpdate()
          }
        })
      }
    },
    uploadFile: function (track, index) {
      let files = this.$refs[`file_${index}`][0]

      if (files && files.files && files.files[0]) {
        let file = files.files[0]

        Vue.set(track, 'uploading', 10)
        this.$forceUpdate()

        Upload.uploadSong(new Date().getTime(), file, event => {
          if (parseInt((event.loaded / event.total) * 100) > 0) {
            Vue.set(
              track,
              'uploading',
              parseInt((event.loaded / event.total) * 100)
            )
          }

          this.$forceUpdate()
        }).then(response => {
          track.file_url = response.data.url
          track.file_url_unencoded = response.data.url
          track.file_name = file.name
          track.file_size = (file.size / 1000000).toFixed(1)
          Vue.set(track, 'file_url', response.data.url)
          Vue.delete(track, 'uploading')
          this.$forceUpdate()
          this.saveDistribution()
        }).catch(error => {
          if (error) {
            console.error('Error uploading track', error, error.response, file)

            this.$refs[`file_${index}`][0].value = ''
            this.$notify({ message: `${this.$t('there_was_an_error_uploading')} Track #${index + 1}, ${this.$t('please_retry_again')}` }, 'warning', 'fa-exclamation')
          }

          Vue.delete(track, 'uploading')
        })
      }
    },
    validateISRC: function (track, index) {
      track.isrc = track.isrc.toUpperCase()

      if (track.isrc && !track.isrc.match(/^[A-Z]{2}-?\w{3}-?\d{2}-?\d{5}$/)) {
        track.isrc = ''
        this.$notify({ message: this.$t('isrc_invalid') }, 'warning')
      }
      _.each(this.distribution.songs, (elem, idx) => {
        if (elem.isrc === track.isrc && index !== idx) {
          track.isrc = ''
          this.$notify({ message: this.$t('isrc_duplicated') }, 'warning')
        }
      })
    },
    onTimeUpdate: function (track, index) {
      let audioElement = window.$(`#audio_${index}`)[0]

      if (this.previewing && audioElement.currentTime >= track.preview_end) {
        audioElement.currentTime = 0
        audioElement.pause()
      }
    },
    onLoadedMetadata: function (track, index) {
      setTimeout(() => {
        let audioElement = window.$(`#audio_${index}`)

        if (audioElement[0] && audioElement[0].duration && !isNaN(audioElement[0].duration)) {
          this.distribution.songs[index].duration = audioElement[0].duration
          this.$forceUpdate()
          // this.initWaveform(index)
        }
      }, 500)
    },
    onAudioLoad: function (index) {
      setTimeout(() => {
        let audioElement = window.$(`#audio_${index}`)
        audioElement.audioPlayer()
      }, 1)
    },
    removeFeaturing: function (track, index) {
      track.featuring.splice(index, 1)
      this.$forceUpdate()
    },
    addFeaturedArtist: function (track, index) {
      if (!this.featuring.name) {
        this.validForm = true
        setTimeout(() => {
          this.validForm = false
        }, 5000)
        return this.$notify({ message: this.$t('type_an_artist_name') }, 'warning', 'fa-exclamation')
      }

      if (this.featuring && this.featuring.name && this.featuring.name.trim().toLowerCase() === this.distribution.artist.name.trim().toLowerCase()) {
        this.validForm = true
        setTimeout(() => {
          this.validForm = false
        }, 5000)
        return this.$notify({ message: this.$t('main_artist_featuring') }, 'warning', 'fa-exclamation')
      }

      if (track.featuring && track.featuring.find(element => element.name.toLowerCase() === this.featuring.name.toLowerCase())) {
        this.validForm = true
        setTimeout(() => {
          this.validForm = false
        }, 5000)
        return this.$notify({ message: this.$t('artist_cannot_duplicated') }, 'warning', 'fa-exclamation')
      }

      let countPrimaryArtist = 0

      _.map(track.featuring, (item, key) => {
        countPrimaryArtist += item.primaryArtist
      })

      if (countPrimaryArtist > 4) {
        return this.$notify({ message: this.$t('not_more_primary_artist') }, 'warning', 'fa-exclamation')
      }

      if (!track.featuring) {
        track.featuring = []
      }

      if (this.featuring.name) {
        this.featuring.name = this.featuring.name.replace(/"|'|\(|\)|'/g, '')
        this.featuring.name = filters.lowerCase(this.featuring.name)
        this.featuring.name = filters.capitalizeText(this.featuring.name)
        this.featuring.name = this.featuring.name.trim()
      }

      track.featuring.push(this.featuring)

      this.featuring = {
        name: '',
        primaryArtist: 0,
        spotify_id: null,
        details: {
          followers: '',
          img: '',
          external_urls: ''
        }
      }

      this.checkSpotifyArtist = false
      window.$(`#checkSpotifyArtist`).prop('checked', false).change()
      window.$(`#checkPrimaryArtist`).prop('checked', false).change()

      this.$forceUpdate()
    },
    removeSong: function (track, index) {
      if (track.file_url) {
        Upload.removeSong('/distribution-songs', track.file_url.split('/').pop()).then(() => {})
      }

      this.trackSectionFlags.splice(index, 1)
      this.trackSectionFlags2.splice(index, 1)
      this.distribution.songs.splice(index, 1)

      this.saveDistribution()
      this.$forceUpdate()
    },
    addTrack: function () {
      if (!this.user.subscription && this.distribution.songs.length >= this.freeTracksLimit) {
        setTimeout(() => {
          return window.$(this.$refs.limitedTracksModal).modal('show')
        }, 100)
      } else {
        this.trackSectionFlags.push(true)
        this.trackSectionFlags2.push(true)

        // Adding default contributors - Album / EP
        songBase.contributors.data = [
          {
            name: songBase.contributors.data[0].name,
            role: 'Composer',
            percentage: 100
          },
          {
            name: songBase.contributors.data[0].name,
            role: 'Producer',
            percentage: 0
          }
        ]
        // Adding default performer - Album / EP
        songBase.performers = [{
          name: songBase.contributors.data[0].name,
          instrument: 'keyboards'
        }]

        this.distribution.songs.push(JSON.parse(JSON.stringify(songBase)))

        setTimeout(() => {
          window.$('.toggle-checkbox[data-toggle]').bootstrapToggle({
            on: this.$t('yes'),
            off: this.$t('no'),
            width: 80
          })
        }, 100)
        this.$forceUpdate()
      }
    },
    goToCover: function () {
      let invalidPercentages = false

      _.each(this.distribution.songs, song => {
        if (song.contributors && song.contributors.data) {
          let total = 0

          _.each(song.contributors.data, item => {
            if (item.percentage && item.percentage > 0) {
              total += item.percentage
            }
          })

          if (total !== 100) {
            invalidPercentages = true
          }
        }
      })

      if (invalidPercentages) {
        return this.$notify({ message: this.$t('assigned_percentage_must') }, 'warning', 'fa-exclamation')
      }

      let missing = {
        file: [],
        title: [],
        mainGenre: [],
        style: [],
        contributor: [],
        contributorDuplicated: [],
        selectHasLyrics: [],
        lyrics: []
      }

      _.each(this.distribution.songs, (song, index) => {
        if (!song.file_url) {
          missing.file.push(`#${index + 1}`)
        }

        if (!song.title) {
          missing.title.push(`#${index + 1}`)
        }

        if (!song.main_genre) {
          missing.mainGenre.push(`#${index + 1}`)
        }

        if (!song.style) {
          missing.style.push(`#${index + 1}`)
        }

        if (!window.$('input[name=checkLyrics' + index + ']:checked').length) {
          missing.selectHasLyrics.push(`#${index + 1}`)
        }

        _.each(song.contributors.data, (contributor) => {
          contributor.name = contributor.name.trim()
        })

        let missingContributorName = _.find(song.contributors.data, contributor => contributor.name.length < 2)
        let missingContributorNameDuplicade = 0

        _.each(song.contributors.data, (contributor, indexC) => {
          _.each(song.contributors.data, (contributorTwo, indexC2) => {
            if (contributor.name.toLowerCase() === contributorTwo.name.toLowerCase().toLowerCase() && contributor.role.toLowerCase() === contributorTwo.role.toLowerCase().toLowerCase() && indexC !== indexC2) missingContributorNameDuplicade = 1
          })
        })

        if (missingContributorName) {
          missing.contributor.push(`#${index + 1}`)
        }

        if (missingContributorNameDuplicade) {
          missing.contributorDuplicated.push(`#${index + 1}`)
        }
      })

      let invalidTitle = _.find(this.distribution.songs, song => {
        return this.isSongTitleInvalid(song)
      })
      let featuringInTitle = _.find(this.distribution.songs, song => {
        return this.featuringInTitle(song)
      })

      let missingPerformanceLanguage = _.find(this.distribution.songs, song => !song.performance_language)

      if (missingPerformanceLanguage) {
        return this.$notify(
          { message: this.$t('select_language_tracks') },
          'warning',
          'fa-exclamation'
        )
      }

      if (featuringInTitle) {
        this.invalidFeaturingWords()
        return
      }

      if (invalidTitle) {
        this.invalidSongTitleWords()
        return
      }

      if (missing.title.length) {
        return this.$notify(
          { message: `${this.$t('missing_track_title')} ${missing.title.join(', ')}` },
          'warning',
          'fa-exclamation'
        )
      }

      if (missing.file.length) {
        return this.$notify(
          { message: `${this.$t('missing_track_audio')} ${missing.file.join(', ')}` },
          'warning',
          'fa-exclamation'
        )
      }

      if (missing.mainGenre.length) {
        return this.$notify(
          { message: `${this.$t('missing_track_genre')} ${missing.mainGenre.join(', ')}` },
          'warning',
          'fa-exclamation'
        )
      }

      if (missing.style.length) {
        return this.$notify(
          { message: `${this.$t('missing_track_style')} ${missing.style.join(', ')}` },
          'warning',
          'fa-exclamation'
        )
      }

      if (missing.selectHasLyrics.length) {
        return this.$notify(
          { message: `${this.$t('missing_track_has_lyrics')} ${missing.selectHasLyrics.join(', ')}` },
          'warning',
          'fa-exclamation'
        )
      }

      if (missing.contributor.length) {
        return this.$notify({ message: `${this.$t('missing_contributor_name')} ${missing.contributor.join(', ')}` }, 'warning', 'fa-exclamation')
      }

      if (missing.contributorDuplicated.length) {
        return this.$notify({ message: `${this.$t('main_artist_contributor_duplicate')} ${missing.contributorDuplicated.join(', ')}` }, 'warning', 'fa-exclamation')
      }

      if (this.distribution.release_type === 'album' && this.distribution.songs.length === 1) {
        return window.$(this.$refs.modalSwitchToAlbum).modal('show')
      }

      _.each(this.distribution.songs, (songs, idxS) => {
        _.each(songs.featuring, (feat, idxF) => {
          delete this.distribution.songs[idxS].featuring[idxF].details
        })
        _.each(songs.contributors.data, (cont, idxC) => {
          delete this.distribution.songs[idxS].contributors.data[idxC].details
        })
      })

      this.saving = true
      this.distribution.complete = 1

      this.saveDistribution(() => {
        this.saving = false
        this.$notify({ message: this.$t('distribution_updated') }, 'success', 'fa fa-check')

        if (this.distribution.reopen_in_progress) {
          this.$router.push(`/distributions/${this.distribution._id}/review`)
        } else {
          this.$router.push(`/distributions/${this.distribution._id}/cover`)
        }
      })
    },
    saveDistribution: function (callback) {
      this.distribution.steps.step_songs = { validate: true }
      this.distribution.fromPage = 'songs'

      _.each(this.distribution.songs, track => {
        delete track.custom_preview
      })

      return Distributions.save(this.distribution)
        .then(response => {
          if (typeof callback === 'function') {
            callback()
          }
        })
        .catch(err => {
          let error = err.response.data.name

          if (error === 'validation_two_words_contributors') {
            this.saving = false
            this.$notify(
              { message: this.$t('validation_two_words_contributors') },
              'warning',
              'fa-exclamation'
            )
          }

          if (error === 'validation_two_characters_contributors') {
            this.saving = false
            this.$notify(
              { message: this.$t('validation_two_characters_contributors') },
              'warning',
              'fa-exclamation'
            )
          }

          if (error === 'release_date_invalid') {
            this.$notify(
              { message: this.$t('release_date_invalid') },
              'danger',
              'fa-exclamation'
            )
          }

          if (error === 'already_exists') {
            this.$notify(
              { message: this.$t('distribution_name_repeated') },
              'danger',
              'fa-exclamation'
            )
          }

          if (error === 'distro_already_reviewed') {
            if (this.$route.params.id) {
              this.$router.push(`/distributions/${this.$route.params.id}/details`)
            } else {
              this.$router.push(`/distributions`)
            }
          }
        })
    },
    handleInputTrackTitle: function (track) {
      let finalLanguage = LANGUAGE_REGEX[this.distribution.language.toLowerCase()]
      // Remove the last character (']') from finalLanguage.source and add the ()[]/.,& symbols and numbers
      finalLanguage = new RegExp(`${finalLanguage.source.slice(0, -1)}()\\[\\]/.,&:\\d]`, 'g')
      track.title = track.title.replace(finalLanguage, '')
    },
    validateSongTitle: function (track, index) {
      track.title = track.title.replace(/  +/g, ' ')

      if (track.title && this.distribution.release_type === 'album') {
        track.title = track.title.replace(/"/g, '')
        track.title = filters.lowerCase(track.title)
        track.title = filters.capitalizeText(track.title)

        if (this.isSongTitleInvalid(track)) {
          this.invalidSongTitleWords()
          return
        }

        if (this.featuringInTitle(track)) {
          this.invalidFeaturingWords()
        }
      }

      if (track.title) {
        let regExp = new RegExp(this.distribution.artist.name, 'gi')

        if (track.title.match(regExp)) {
          track.title = track.title.replace(regExp, '').trim()
        }
      }
    },
    validateSongLyrics: function (track, index) {
      if (track.lyrics) {
        track.lyrics = track.lyrics.replace(/([\u2700-\u27BF]|[\uE000-\uF8FF]|\uD83C[\uDC00-\uDFFF]|\uD83D[\uDC00-\uDFFF]|[\u2011-\u26FF]|\uD83E[\uDD10-\uDDFF])/g, '')
      }
    },
    isSongTitleInvalid: function (track) {
      return (track.title && (track.title.match(/album$|album\s|\sep\s|\sep$|^single\s|\ssingle$/gi)))
    },
    invalidSongTitleWords: function () {
      window.swal(this.$t('distribution_process.invalid_word'), this.$t('distribution_process.album_ep_error_msg'), {
        closeOnClickOutside: false,
        className: 'icon-modal',
        icon: 'warning',
        buttons: {
          confirm: {
            text: this.$t('i_understand'),
            className: 'btn btn-warning'
          }
        }
      })
    },
    featuringInTitle: function (track) {
      let isInvalid = false

      if (track.title) {
        if (track.title.match(/\sft\s|\(ft\.|ft\.|\sft\.|\(feat\.|feat\.|featuring\s|\sfeat\s|^feat\./gi)) {
          isInvalid = true
        }
      }

      return isInvalid
    },
    invalidFeaturingWords: function () {
      window.swal(this.$t('distribution_process.invalid_word'), `${this.$t('distribution_process.featuring_error_msg')}`, {
        closeOnClickOutside: false,
        className: 'icon-modal',
        icon: 'warning',
        buttons: {
          confirm: {
            text: this.$t('i_understand'),
            className: 'btn btn-warning'
          }
        }
      })
    },
    hasReopeningReason: function () {
      return (this.distribution.status === 0 && this.reopeningOptions.includes(this.distribution.reopening_reason))
    },
    setPrimaryArtist: function () {
      this.featuring.primaryArtist = (window.$(`#checkPrimaryArtist`).prop('checked')) ? 0 : 1
    },
    closeFeaturingModal: function () {
      this.featuring = {
        name: '',
        primaryArtist: 0,
        spotify_id: null,
        details: {
          followers: '',
          img: '',
          external_urls: ''
        }
      }
      this.songIndex = 0
      window.$(`#checkPrimaryArtist`).prop('checked', false).change()
      this.checkSpotifyArtist = false
      window.$(`#checkSpotifyArtist`).prop('checked', false).change()
      this.addOther = false
      this.featStep = 0
    },
    showFeaturingModal: function (index) {
      this.featuring = {
        name: '',
        primaryArtist: 0,
        spotify_id: null,
        details: {
          followers: '',
          img: '',
          external_urls: ''
        }
      }
      this.songIndex = index || 0
      this.featStep = 0
      setTimeout(() => {
        window.$('.toggle-checkbox[data-toggle]').bootstrapToggle({
          on: this.$t('yes'),
          off: this.$t('no'),
          width: 80
        })
      }, 100)
      this.$forceUpdate()
      window.$(this.$refs.modalFeaturings).modal('show')
    },
    addFeaturingNoSpotify: function () {
      if (!this.featuring.name) {
        this.validForm = true
        setTimeout(() => {
          this.validForm = false
        }, 5000)
        return this.$notify({ message: this.$t('type_an_artist_name') }, 'warning', 'fa-exclamation')
      }

      this.featuring.create_spotify_profile = (this.spotifyArtists.length) ? 1 : 0

      this.addFeaturedArtist(this.distribution.songs[this.songIndex], this.songIndex)
      if (!this.addOther) {
        window.$(this.$refs.modalFeaturings).modal('hide')
      } else {
        this.showFeaturingModal()
      }
      this.closeFeaturingModal()
    },
    validateFeaturingName: function (featuring) {
      featuring.name = featuring.name.replace(/  +/g, ' ')
    },
    closeContributirModal: function (index, contributorIndex) {
      this.contributors = {
        selectedName: '',
        selectedRole: '',
        selectedPercentage: 0,
        selectedContributorEmail: ''
      }
      this.checkingContributor = false
      this.contributorNotFound = false
      this.isValidContributor = false
      this.contributorIndex = null
      this.loadingSpotifyArtistsContribuitor = false
      window.$(`#checkSpotifyArtistContribuitor`).prop('checked', false).change()
      this.checkSpotifyArtistContribuitor = false
      this.spotifyArtistsContribuitor = []
      this.modeContribuitor = 0
      this.showMessageCover = false
    },
    closePerformersModal: function (index, contributorIndex) {
      this.performers = {
        selectedName: '',
        selectedRole: ''
      }
      this.contributorIndex = null
      this.showMessageCover = false
    },
    showContributirModal: function (index, contributor, contributorIndex) {
      this.contributors = {
        selectedName: '',
        selectedRole: '',
        selectedPercentage: 0,
        selectedContributorEmail: ''
      }

      this.contributorNotFound = false
      this.isValidContributor = false
      this.songIndex = index || 0
      this.contributorIndex = (contributorIndex === 0 || contributorIndex > 0) ? contributorIndex : null

      if (contributor) {
        this.contributors = {
          selectedName: contributor.name,
          selectedRole: contributor.role,
          selectedPercentage: contributor.percentage,
          selectedContributorEmail: contributor.email || '',
          edit: 1
        }

        if (contributor.email) {
          this.isValidContributor = true
        }
      }

      if (contributorIndex === 0 && (!contributor || !contributor.email)) {
        this.contributors.selectedContributorEmail = this.user.email
        this.isValidContributor = true
      }

      window.$(this.$refs.modalContributors).modal('show')
    },
    showPerfomersModal: function (index, contributor, contributorIndex) {
      this.performers = {
        selectedName: '',
        selectedRole: ''
      }

      this.songIndex = index || 0
      this.contributorIndex = (contributorIndex === 0 || contributorIndex > 0) ? contributorIndex : null

      if (contributor) {
        this.performers = {
          selectedName: contributor.name,
          selectedRole: contributor.instrument,
          edit: 1
        }
      }

      window.$(this.$refs.modalPerformers).modal('show')
    },
    addContributor: function (artist = {}) {
      this.distribution.songs[this.songIndex].contributors.selectedName = this.contributors.selectedName
      this.distribution.songs[this.songIndex].contributors.selectedRole = this.contributors.selectedRole

      if (this.user.subscription) {
        if (this.contributors.selectedContributorEmail.toLowerCase() === this.user.email) {
          this.contributors.selectedContributorEmail = ''
        }

        if (this.contributors.selectedContributorEmail && this.contributors.selectedContributorEmail.toLowerCase() === (this.user.email || '').toLowerCase()) {
          return this.$notify({ message: this.$t('cannot_divide_yourself') }, 'warning', 'fa-exclamation')
        }

        this.distribution.songs[this.songIndex].contributors.selectedPercentage = parseInt(this.contributors.selectedPercentage || 0)
        this.distribution.songs[this.songIndex].contributors.selectedContributorEmail = this.contributors.selectedContributorEmail || ''
      }

      if (!this.contributors.selectedName) {
        this.validForm = true
        setTimeout(() => {
          this.validForm = false
        }, 5000)
        return this.$notify({ message: this.$t('name_not_empty') }, 'warning', 'fa-exclamation')
      }

      if (!this.contributors.selectedRole) {
        this.validFormSelect = true
        setTimeout(() => {
          this.validFormSelect = false
        }, 5000)
        return this.$notify({ message: this.$t('select_role') }, 'warning', 'fa-exclamation')
      }

      if (this.contributors.selectedPercentage > 100) {
        this.validFormSelect = true
        setTimeout(() => {
          this.validFormSelect = false
        }, 5000)
        return this.$notify({ message: this.$t('invalid_percentage') }, 'warning', 'fa-exclamation')
      }

      if (this.contributors.selectedName) {
        let splitName = this.contributors.selectedName.replace(/\s+/gi, ' ')
        splitName = splitName.split(' ')
        if (splitName.length < 2) {
          setTimeout(() => {
            this.validForm = false
          }, 5000)
          return this.$notify({ message: this.$t('validation_two_words_contributors') }, 'warning', 'fa-exclamation')
        } else if (splitName[1].length < 2) {
          setTimeout(() => {
            this.validForm = false
          }, 5000)
          return this.$notify({ message: this.$t('validation_two_characters_contributors') }, 'warning', 'fa-exclamation')
        }
      }

      if (this.contributors.selectedContributorEmail &&
        this.contributors.selectedContributorEmail.toLowerCase() === this.user.email.toLowerCase()
      ) {
        this.contributors.selectedContributorEmail = ''
      }

      if (this.contributors.selectedContributorEmail && this.contributorNotFound) {
        return this.$notify({ message: this.$t('existent_user_needed') }, 'warning', 'fa-exclamation')
      }

      if (!this.contributors.edit) {
        this.setContributor(this.distribution.songs[this.songIndex], this.songIndex, artist)

        if (!this.addOther) {
          window.$(this.$refs.modalContributors).modal('hide')
          this.closeContributirModal()
        }
      } else {
        this.distribution.songs[this.songIndex].contributors.data[this.contributorIndex].name = this.contributors.selectedName
        this.distribution.songs[this.songIndex].contributors.data[this.contributorIndex].role = this.contributors.selectedRole

        if (this.user.subscription) {
          this.distribution.songs[this.songIndex].contributors.data[this.contributorIndex].percentage = parseInt(this.contributors.selectedPercentage || 0)
          this.distribution.songs[this.songIndex].contributors.data[this.contributorIndex].email = this.contributors.selectedContributorEmail || ''
        }

        if (artist.uri) {
          this.distribution.songs[this.songIndex].contributors.data[this.contributorIndex].name = artist.name
          this.distribution.songs[this.songIndex].contributors.data[this.contributorIndex].spotify_id = artist.uri
          this.distribution.songs[this.songIndex].contributors.data[this.contributorIndex].details = {
            followers: (artist.followers) ? artist.followers.total : '',
            img: (artist.images.length) ? artist.images[0].url : this.defaultImage,
            external_urls: (artist.external_urls) ? artist.external_urls.spotify : ''
          }
        }

        window.$(this.$refs.modalContributors).modal('hide')
        this.closeContributirModal()
      }

      this.royaltiesAssignedPercentage = 0

      _.each(this.distribution.songs[this.songIndex].contributors.data, contributor => {
        this.royaltiesAssignedPercentage += parseInt(contributor.percentage || 0)
      })

      this.royaltiesAssignedPercentage += parseInt(this.contributors.selectedPercentage)

      this.contributors = {
        selectedName: '',
        selectedRole: '',
        percentage: 0,
        email: ''
      }

      this.invalidNameReason = ''
    },
    addPerformers: function (artist = {}) {
      this.distribution.songs[this.songIndex].performers.selectedName = this.performers.selectedName
      this.distribution.songs[this.songIndex].performers.selectedRole = this.performers.selectedRole

      if (!this.performers.selectedName) {
        this.validForm = true
        setTimeout(() => {
          this.validForm = false
        }, 5000)
        return this.$notify({ message: this.$t('name_not_empty') }, 'warning', 'fa-exclamation')
      }

      if (!this.performers.selectedRole) {
        this.validFormSelect = true
        setTimeout(() => {
          this.validFormSelect = false
        }, 5000)
        return this.$notify({ message: this.$t('select_instrument') }, 'warning', 'fa-exclamation')
      }

      if (this.performers.selectedName) {
        let splitName = this.performers.selectedName.replace(/\s+/gi, ' ')
        splitName = splitName.split(' ')
        if (splitName.length < 2) {
          setTimeout(() => {
            this.validForm = false
          }, 5000)
          return this.$notify({ message: this.$t('validation_two_words_contributors') }, 'warning', 'fa-exclamation')
        } else if (splitName[1].length < 2) {
          setTimeout(() => {
            this.validForm = false
          }, 5000)
          return this.$notify({ message: this.$t('validation_two_characters_contributors') }, 'warning', 'fa-exclamation')
        }
      }

      if (!this.performers.edit) {
        this.setPerformers(this.distribution.songs[this.songIndex])

        if (!this.addOther) {
          window.$(this.$refs.modalPerformers).modal('hide')
          this.closePerformersModal()
        }
      } else {
        this.distribution.songs[this.songIndex].performers[this.contributorIndex].name = this.performers.selectedName
        this.distribution.songs[this.songIndex].performers[this.contributorIndex].instrument = this.performers.selectedRole

        window.$(this.$refs.modalPerformers).modal('hide')
        this.closePerformersModal()
      }

      this.performers = {
        selectedName: '',
        selectedRole: ''
      }

      this.invalidNamePerformerReason = ''
    },
    setContributor: function (track, index, artist = {}) {
      if (track.contributors.selectedName && track.contributors.selectedRole) {
        if (!this.user.subscription && track.contributors.data.length >= 4) {
          return window.$(this.$refs.limitedDistrosModal).modal('show')
        } else if (this.user.subscription && track.contributors.data.length >= 10) {
          return window.$(this.$refs.limitedContributorsProModal).modal('show')
        }

        if (track.contributors.selectedName.toLowerCase() === this.distribution.artist.name.toLowerCase()) {
          track.contributors.selectedName = ''
          track.contributors.selectedRole = ''
          track.contributors.selectedContributorEmail = ''
          track.contributors.selectedPercentage = 0
          this.validForm = true
          setTimeout(() => {
            this.validForm = false
          }, 5000)
          return this.$notify({ message: this.$t('main_artist_contributor') }, 'warning', 'fa-exclamation')
        }

        if (track.contributors.data.find(element => element.name.toLowerCase() === track.contributors.selectedName.toLowerCase() && element.role.toLowerCase() === track.contributors.selectedRole.toLowerCase())) {
          track.contributors.selectedName = ''
          track.contributors.selectedRole = ''
          track.contributors.selectedContributorEmail = ''
          track.contributors.selectedPercentage = 0
          this.validForm = true
          setTimeout(() => {
            this.validForm = false
          }, 5000)
          return this.$notify({ message: this.$t('main_artist_contributor_duplicate') }, 'warning', 'fa-exclamation')
        }

        let info = {
          name: track.contributors.selectedName.trim(),
          role: track.contributors.selectedRole,
          percentage: parseInt(track.contributors.selectedPercentage || 0),
          email: track.contributors.selectedContributorEmail || ''
        }

        if (artist.uri) {
          info.name = artist.name
          info.spotify_id = artist.uri
          info.details = {
            followers: (artist.followers) ? artist.followers.total : '',
            img: (artist.images.length) ? artist.images[0].url : this.defaultImage,
            external_urls: (artist.external_urls) ? artist.external_urls.spotify : ''
          }
        }

        track.contributors.data.push(info)

        if (track.contributors.selectedRole === 'Lyricist') {
          track.hasLyrics = true
          window.$('input[name=checkLyrics' + index + '][value="1"]').prop('checked', true)
        }

        track.contributors.selectedName = ''
        track.contributors.selectedRole = ''
        track.contributors.selectedContributorEmail = ''
        track.contributors.selectedPercentage = 0
        track.contributors.showInput = false
      }
      this.$forceUpdate()
    },
    setPerformers: function (track) {
      if (track.performers.selectedName && track.performers.selectedRole) {
        let info = {
          name: track.performers.selectedName,
          instrument: track.performers.selectedRole
        }

        track.performers.push(info)
        track.performers.selectedName = ''
        track.performers.selectedRole = ''
      }
      this.$forceUpdate()
    },
    removeContributor: function (track, index, indexSong) {
      let data = this.distribution.songs[indexSong].contributors.data || []

      if (track.contributors.data[index].role === 'Lyricist') {
        this.countLyricist = 0
        let performanceLanguage = (window.$('input[name=checkLyrics' + indexSong + ']').prop('checked')) ? 'EN' : 'ZXX'

        _.map(data, (item, key) => {
          this.countLyricist += (item.role === 'Lyricist') ? 1 : 0
        })

        if (this.countLyricist < 2) {
          track.contributors.data = _.filter(data, (contributor, contributorIndex) => contributor.role !== this.lyricist)
          track.hasLyrics = false
          Vue.set(track, 'performance_language', performanceLanguage)
          track.performance_language = performanceLanguage
          window.$('input[name=checkLyrics' + indexSong + '][value="0"]').prop('checked', true)
          this.distribution.songs[indexSong].contributors.data = _.filter(data, (contributor, contributorIndex) => contributor.role !== this.lyricist)
        } else {
          track.contributors.data.splice(index, 1)
        }
      } else {
        track.contributors.data.splice(index, 1)
      }
      this.firstLyricist = null
      _.map(data, (item, key) => {
        if (!this.firstLyricist && item.role === 'Lyricist') {
          this.firstLyricist = key
        }
      })

      this.royaltiesAssignedPercentage = 0

      _.each(this.distribution.songs[this.songIndex].contributors.data, contributor => {
        this.royaltiesAssignedPercentage += parseInt(contributor.percentage || 0)
      })

      this.royaltiesAssignedPercentage += parseInt(this.contributors.selectedPercentage)

      this.$forceUpdate()
    },
    removePerformers: function (track, index, indexSong) {
      track.performers.splice(index, 1)
      this.$forceUpdate()
    },
    validateContributorName: function (contributors) {
      contributors.selectedName = contributors.selectedName.replace(/  +/g, ' ')
    },
    validatePerformerName: function (performers) {
      performers.selectedName = performers.selectedName.replace(/  +/g, ' ')
    },
    toggleTrackSection: function (index) {
      this.trackSectionFlags[index] = !this.trackSectionFlags[index]
      this.$forceUpdate()
    },
    canEditMetadata: function () {
      return this.metadataPage && this.lockInputs
    },
    cleanAudioData: function () {
      if (this.distribution.songs[this.songIndex].file_url) {
        Upload.removeSong('/distribution-songs', this.distribution.songs[this.songIndex].file_url.split('/').pop()).then(() => {})
      }

      this.distribution.songs[this.songIndex].file_name = ''
      this.distribution.songs[this.songIndex].file_size = ''
      this.distribution.songs[this.songIndex].file_url = ''
      this.distribution.songs[this.songIndex].duration = 0
      this.saveDistribution()
      this.$forceUpdate()
    },
    setDefaultContributor: function (song, role) {
      let name = ''

      if (this.distribution.artist.legal_name && this.distribution.artist.legal_last_name) {
        name = `${this.distribution.artist.legal_name.trim()} ${this.distribution.artist.legal_last_name.trim()}`
      } else if (this.user.name && this.user.last_name) {
        name = `${this.user.name.trim()} ${this.user.last_name.trim()}`
      } else {
        name = this.distribution.artist.name.trim()
      }

      if (song.contributors.data.length > 0 && role === 'Lyricist') {
        song.contributors.data.push({
          name: name,
          role: role,
          percentage: 0
        })
      } else if (song.contributors && song.contributors.data && song.contributors.data.length === 0 && role === 'Composer') {
        song.contributors.data.push({
          name: name,
          role: role,
          percentage: 100
        })
      }

      this.countLyricist = 0
      this.firstLyricist = null
      _.map(song.contributors.data, (item, key) => {
        this.countLyricist += (item.role === 'Lyricist') ? 1 : 0

        if (!this.firstLyricist && item.role === 'Lyricist') {
          this.firstLyricist = key
        }
      })
    },
    setDefaultPerformerInstrument: function (song, instrument) {
      let name = ''

      if (this.distribution.artist.legal_name && this.distribution.artist.legal_last_name) {
        name = `${this.distribution.artist.legal_name.trim()} ${this.distribution.artist.legal_last_name.trim()}`
      } else if (this.user.name && this.user.last_name) {
        name = `${this.user.name.trim()} ${this.user.last_name.trim()}`
      } else {
        name = this.distribution.artist.name.trim()
      }
      song.performers.push({
        name: name,
        instrument: instrument
      })
    },
    setDefaultProductionEngineering: function (song, role) {
      let name = ''

      if (this.distribution.artist.legal_name && this.distribution.artist.legal_last_name) {
        name = `${this.distribution.artist.legal_name.trim()} ${this.distribution.artist.legal_last_name.trim()}`
      } else if (this.user.name && this.user.last_name) {
        name = `${this.user.name.trim()} ${this.user.last_name.trim()}`
      } else {
        name = this.distribution.artist.name.trim()
      }

      song.contributors.data.push({
        name: name,
        role: role,
        percentage: 0
      })
    },
    validateRemoveContributor: function (contributor, contributors) {
      let requiredRoles = ['Composer', 'Lyricist']
      return requiredRoles.includes(contributor.role) && contributors === 1
    },
    findSpotifyArtist: function (urlSearch = false) {
      if (this.featuring.name.trim() === '') {
        this.$refs.featName.focus()
        return this.$notify({ message: this.$t('type_name') }, 'warning', 'fa-exclamation')
      }
      if (this.featuring && this.featuring.name && this.featuring.name.trim().toLowerCase() === this.distribution.artist.name.trim().toLowerCase()) {
        this.$refs.featName.focus()
        return this.$notify({ message: this.$t('main_artist_featuring') }, 'warning', 'fa-exclamation')
      }
      this.featStep = 1
      this.spotifyArtists = []
      this.spotifyPrev = null
      this.spotifyNext = null
      this.loadingSpotifyArtists = true
      this.featuringName = '[' + this.featuring.name + ']'
      Artists.checkArtistName(this.featuring.name, urlSearch).then(response => {
        if (response.data.results.length) {
          response.data.results.sort(function (a, b) {
            if (a.followers.total > b.followers.total) { return -1 }
            if (a.followers.total < b.followers.total) { return 1 }
            return 0
          })
          this.spotifyPrev = response.data.previous
          this.spotifyNext = response.data.next
          this.spotifyArtists = response.data.results
        }
        setTimeout(() => {
          window.$('.toggle-checkbox[data-toggle]').bootstrapToggle({
            on: this.$t('yes'),
            off: this.$t('no'),
            width: 80
          })
        }, 100)
        this.loadingSpotifyArtists = false
        this.$forceUpdate()
      }).catch(e => {
        this.loadingSpotifyArtists = false
        this.$forceUpdate()
      })
    },
    setCheckSpotifyArtist: function () {
      this.checkSpotifyArtist = !window.$(`#checkSpotifyArtist`).prop('checked')
    },
    selectSpotifyArtist: function (artist) {
      if (this.distribution.artist.spotify_id !== artist.uri) {
        this.featuring.name = artist.name
        this.featuring.spotify_id = artist.uri
        this.featuring.details = {
          followers: (artist.followers) ? artist.followers.total : '',
          img: (artist.images.length) ? artist.images[0].url : this.defaultImage,
          external_urls: (artist.external_urls) ? artist.external_urls.spotify : ''
        }
        this.addFeaturedArtist(this.distribution.songs[this.songIndex], this.songIndex)
        if (!this.addOther) {
          window.$(this.$refs.modalFeaturings).modal('hide')
        } else {
          this.showFeaturingModal()
        }
        this.closeFeaturingModal()
      } else {
        this.$notify({ message: this.$t('same_spotify_ids') }, 'warning', 'fa-exclamation')
      }
    },
    changeShowPotify: function (contribuitor, urlSearch = false) {
      if (contribuitor.selectedName !== '') {
        this.loadingSpotifyArtistsContribuitor = true
        this.modeContribuitor = 1
        this.spotifyPrevContribuitor = null
        this.spotifyNextContribuitor = null
        Artists.checkArtistName(contribuitor.selectedName, urlSearch).then(response => {
          if (response.data.results.length) {
            response.data.results.sort(function (a, b) {
              if (a.followers.total > b.followers.total) { return -1 }
              if (a.followers.total < b.followers.total) { return 1 }
              return 0
            })
            this.spotifyPrevContribuitor = response.data.previous
            this.spotifyNextContribuitor = response.data.next
            this.spotifyArtistsContribuitor = response.data.results
          }
          setTimeout(() => {
            window.$('.toggle-checkbox[data-toggle]').bootstrapToggle({
              on: this.$t('yes'),
              off: this.$t('no'),
              width: 80
            })
          }, 100)
          this.loadingSpotifyArtistsContribuitor = false
          this.$forceUpdate()
        }).catch(e => {
          this.loadingSpotifyArtistsContribuitor = false
          this.$forceUpdate()
        })
      } else {
        alert('Empty name')
      }
    },
    setCheckSpotifyArtistContribuitor: function () {
      this.checkSpotifyArtistContribuitor = !window.$(`#checkSpotifyArtistContribuitor`).prop('checked')
    },
    switchToAlbum: function () {
      this.loading = true
      this.distribution.release_type = 'single'
      this.distribution.songs[0].title = this.distribution.title
      this.distribution.songs[0].main_genre = this.distribution.main_genre
      setTimeout(() => {
        this.distribution.songs[0].style = this.distribution.style
        this.$forceUpdate()
        this.goToCover()
      }, 100)
    },
    revertMetadata: function () {
    },
    keyChanges: function (base, object) {
      const changes = {}

      function walkObject (base, object, path = '') {
        for (const key of Object.keys(base)) {
          const currentPath = path === '' ? key : `${path}.${key}`

          if (object[key] === undefined) {
            changes[currentPath] = '-'
          }
        }

        for (const [key, value] of Object.entries(object)) {
          const currentPath = Array.isArray(object) ? path + `[${key}]` : path === '' ? key : `${path}.${key}`

          if (base[key] === undefined) {
            _.each(object[key], (elem, idx) => {
              changes[`${currentPath}.${idx}`] = elem
            })
          } else if (value !== base[key]) {
            if (typeof value === 'object' && typeof base[key] === 'object') {
              walkObject(base[key], value, currentPath)
            } else {
              changes[currentPath] = object[key]
            }
          }
        }
      }

      walkObject(base, object)

      return changes
    },
    getArtist: function () {
      return Artists.getAll().then(response => {
        this.artists = _.map(response.data, item => {
          return { id: item._id, text: item.name }
        })
      })
    },
    getDistribution: function () {
      this.versions = [
        { id: '', text: this.$t('original') },
        { id: 'Cover', text: this.$t('cover_version') },
        { id: 'Live Version', text: this.$t('songs_live_version') },
        { id: 'Radio Edit', text: this.$t('radio_edit') },
        { id: 'Instrumental Version', text: this.$t('instrumental') },
        { id: 'Extended Version', text: this.$t('extended_version') },
        { id: 'Remix', text: this.$t('remix') }
      ]

      return Distributions.get(this.$route.params.id).then(response => {
        this.distribution = response.data
        this.distributionOriginal = JSON.parse(JSON.stringify(this.distribution))

        if (this.distribution.status > 0) {
          if (this.$route.params.id) {
            return this.$router.push(`/distributions/${this.$route.params.id}/details`)
          } else {
            return this.$router.push(`/distributions`)
          }
        } else if (this.hasReopeningReason()) {
          if (!this.distribution.reopen_in_progress) {
            return this.$router.push(`/distributions/${this.$route.params.id}/reopening-details`)
          }
        }

        songBase.main_genre = this.distribution.main_genre
        songBase.style = this.distribution.style
        this.trackSectionFlags = []
        this.trackSectionFlags2 = []

        this.setDefaultContributor(songBase, this.composer)

        if (!this.distribution.songs || !this.distribution.songs.length) {
          let song = JSON.parse(JSON.stringify(songBase))
          song.title = this.distribution.title
          Vue.set(this.distribution, 'songs', [song])
        }

        _.each(this.distribution.songs, (song, index) => {
          if (song.featuring) {
            if (typeof song.featuring === 'string') {
              song.featuring = song.featuring.split(',')
              _.each(song.featuring, (item, index) => {
                let itemnew = item.trim()
                song.featuring[index] = {}
                song.featuring[index].name = itemnew
              })
            } else {
              _.each(song.featuring, async (feat, idxF) => {
                if (feat.spotify_id) {
                  let spotifyId = feat.spotify_id.replace('spotify:artist:', '')
                  let responseSpotify = await Artists.getSpotifyArtist(spotifyId)

                  this.distribution.songs[index].featuring[idxF].details = {
                    followers: (responseSpotify.data.followers) ? responseSpotify.data.followers.total : '',
                    img: (responseSpotify.data.images[0]) ? responseSpotify.data.images[0].url : '',
                    external_urls: (responseSpotify.data.external_urls) ? responseSpotify.data.external_urls.spotify : ''
                  }
                  this.$forceUpdate()
                }
              })
            }
          }

          if (song.contributors && song.contributors.data) {
            this.royaltiesAssignedPercentage = 0

            if (!song.contributors.data[0].percentage) {
              song.contributors.data[0].percentage = 100
            }

            _.each(song.contributors.data, async (cont, idxC) => {
              this.royaltiesAssignedPercentage += parseInt(cont.percentage || 0)

              if (cont.spotify_id) {
                let spotifyId = cont.spotify_id.replace('spotify:artist:', '')
                let responseSpotify = await Artists.getSpotifyArtist(spotifyId)

                this.distribution.songs[index].contributors.data[idxC].details = {
                  followers: (responseSpotify.data.followers) ? responseSpotify.data.followers.total : '',
                  img: (responseSpotify.data.images[0]) ? responseSpotify.data.images[0].url : '',
                  external_urls: (responseSpotify.data.external_urls) ? responseSpotify.data.external_urls.spotify : ''
                }
                this.$forceUpdate()
              }
            })
          }

          if (!song.performance_language) {
            song.performance_language = 'ZXX'
          }

          if (!song.main_genre) {
            song.main_genre = this.distribution.main_genre
            song.style = this.distribution.style
          }

          if (!song.contributors) {
            song.contributors = {
              showInput: false,
              selectedName: '',
              selectedRole: '',
              selectedContributorEmail: '',
              selectedPercentage: 0,
              data: []
            }
          } else {
            song.contributors.showInput = false
          }

          if (!song.performers) {
            song.performers = []
          }

          if (song.contributors && song.contributors.data.length && !song.contributors.data[0].percentage) {
            song.contributors.data[0].percentage = 100
          }

          this.trackSectionFlags.push(index === 0)
          this.trackSectionFlags2.push(index === 0)

          if (!song.style || !_.find(this.genresSource[song.main_genre], subgenre => subgenre === song.style)) {
            song.style = song.main_genre
          }

          if (!_.find(song.contributors.data, contributor => contributor.role === this.composer)) {
            this.setDefaultContributor(song, this.composer)
          }

          if (!_.find(song.contributors.data, contributor => contributor.role === this.productionEngineering)) {
            this.setDefaultProductionEngineering(song, this.productionEngineering)
          }

          if (song.performers && song.performers.length === 0) {
            if (!_.find(song.performers, performer => performer.instrument === this.performerInstrument)) {
              this.setDefaultPerformerInstrument(song, this.performerInstrument)
            }
          }

          if (song.hasLyrics && !_.find(song.contributors.data, contributor => contributor.role === this.lyricist)) {
            this.setDefaultContributor(song, this.lyricist)
          }

          if (this.distribution.steps && this.distribution.steps.step_songs && this.distribution.steps.step_songs.validate) {
            setTimeout(() => {
              let valueCheck = song.hasLyrics ? '1' : '0'
              window.$(`input[name=checkLyrics${index}][value="${valueCheck}"]`).prop('checked', true).change()
              if (song.explicit) {
                window.$(`#checkExplicit${index}`).prop('checked', true).change()
              }
            }, 100 * (index + 1))
          }
          this.$forceUpdate()
        })

        setTimeout(() => {
          window.$('.toggle-checkbox[data-toggle]').bootstrapToggle({
            on: this.$t('yes'),
            off: this.$t('no'),
            width: 80
          })
        }, 300)

        if (this.distribution.release_type === 'single') {
          setTimeout(() => {
            this.distribution.songs[0].title = this.distribution.title
            this.distribution.songs[0].main_genre = this.distribution.main_genre
            setTimeout(() => {
              this.distribution.songs[0].style = this.distribution.style
            }, 100)
            this.$forceUpdate()
          }, 500)
        }

        if ((this.distribution.songs && this.distribution.songs.length && !this.distribution.songs.title) && this.distribution.release_type === 'album') {
          setTimeout(() => {
            window.$('#song_0').trigger('click')
          }, 1500)
        }
      })
    },
    handleInput: function (fromSourceComponent) {
      // This regex now handles both the detection of emojis and the detection of unwanted symbols like arrows, punctuation, and pictographs. It supports inputs in all languages. Suggestiong provided by IA
      const emojiRegex = new RegExp('[\\p{Emoji}\\p{Sc}\\p{Sm}\\p{Sk}\\p{So}\\p{P}\\u2190-\\u21FF\\u2700-\\u27BF\\u207D\\u207E\\u208D\\u208E]', 'gu')

      if (fromSourceComponent === 'featNameInput') {
        const emojiRegexWithNumbers = new RegExp('[^\\p{L}0-9\\s\\u2190-\\u21FF\\u2700-\\u27BF\\u207D\\u207E\\u208D\\u208E]', 'gu')
        this.featuring.name = this.featuring.name.replace(emojiRegexWithNumbers, '')
      } else if (fromSourceComponent === 'selectedName') {
        const newValue = this.contributors[fromSourceComponent].replace(emojiRegex, '')
        this.contributors[fromSourceComponent] = newValue // Update the model
      } else if (fromSourceComponent === 'selectedNamePerformers') {
        this.performers['selectedName'] = this.performers['selectedName'].replace(emojiRegex, '')
      }
    }
  },
  beforeDestroy: function () {
    _.each(this.wavesurfer, item => {
      if (item && item.destroy) {
        item.destroy()
      }
    })
  },
  created: function () {
    this.user = this.$user
    this.lang = this.$i18n.locale

    Configuration.get({fields: ['reopen_reasons_fields', 'free_tracks_limit']}).then(response => {
      this.reopeningOptions = _.map(response.data.reopen_reasons_fields, (elem, key) => key)
      this.freeTracksLimit = response.data.free_tracks_limit || 3
    })

    if (!this.user.subscription && !this.user.custom_label) {
      this.isPro = false
    } else {
      this.isPro = true
    }

    if (this.lang === 'es') {
      this.addContributorLang = 'Colaborador'
      this.contributorsOptions = _.map(contributorsRoles, (role, key) => {
        return { id: key, text: role['es'] }
      })
    } else {
      this.addContributorLang = 'Contributor'
      this.contributorsOptions = _.map(contributorsRoles, (role, key) => {
        return { id: key, text: role['en'] }
      })
    }

    this.performersOptions = performersRoles.map(role => ({
      ...role, // Spread the original properties
      text: this.$t(role.text) // Apply the translation function to the text property
    }))
    this.getArtist().then(() => {
      if (this.$route.params.id) {
        this.getDistribution().then(() => {
          this.$forceUpdate()
        })
      }
    })

    User.checkTutorial('explicit_explainer').then(response => {
      if (!response.data.show_tutorial) {
        this.explicitAlertShown = true
      }
    })

    this.$eventBus.$on('setSingleTitleOnSong', (title) => {
      this.distribution.songs[0].title = title
    })

    Public.getLocationSettings().then(responseSettings => {
      this.locationSettings = responseSettings.data

      if (this.locationSettings.hide_basic && !this.user.subscription) {
        return this.$router.push(`/upgrade?from=songs_limited_location`)
      }
    })
  },
  mounted: function () {
    // if (!this.user.subscription) {
    //   setTimeout(() => {
    //     (window.adsbygoogle = window.adsbygoogle || []).push({})
    //   }, 1200)
    // }

    window.scrollTo(0, 0)
  },
  watch: {
    setSongStyle: function (currentValues, oldValues) {
      let newVal = currentValues.filter(obj => oldValues.indexOf(obj) === -1)

      this.distribution.songs.filter(song => {
        if (song.main_genre === newVal.toString()) {
          if (!_.find(this.genresSource[song.main_genre], subgenre => subgenre === song.style)) {
            song.style = song.main_genre
          }
        }
      })
    },
    'distribution.songs': {
      handler: function (newVal) {
        let diffs = this.keyChanges(this.distributionOriginal.songs, newVal)

        _.each(diffs, (elem, idx) => {
          if (idx.indexOf('version') !== -1) {
            if (elem === 'Cover') {
              this.showMessageCover = true
              this.showContributirModal(parseInt(idx.match(/\d/g).join('')))
            }
          }
        })
        this.distributionOriginal.songs = JSON.parse(JSON.stringify(newVal))
      },
      deep: true
    }
  },
  components: {
    Select2,
    DistributionProgress
  }
}
</script>
<style lang="scss" scoped>
#distribution-image {
  width: 200px;
  height: 200px;
  border-radius: 3px;
}
.contributor{
  height: 100%;
}

.not-pl{
  padding-left: 0px;
}

.position-relative {
  border-radius: 5px;
  box-shadow: 0px 2px 5px rgba(200, 200, 200, 0.8);
  position: relative;
  top: 0px;
  transition: all 0.1s ease;
  .card-custom {
    background-color: #fff;
    padding: 10px 10px 20px;
    .icon-card{
      font-size: 21px;
    }
    .title-card{
      font-size: 13px;
      line-height: 13px;
    }
    .subTitle-card{
      font-size: 10px;
    }
  }
  &:hover{
    top: -1px;
    box-shadow: 0px 2px 5px rgb(148, 148, 148);
  }
}

.fasAnimate.fa-plus{
  transition: all 0.4s ease;
  transform: rotate(0deg);
  opacity: 1;
}

.active .fasAnimate.fa-plus{
  transform: rotate(90deg);
  opacity: 0;
}

.fasAnimate.fa-minus{
  transition: all 0.4s ease;
  transform: rotate(-90deg);
  opacity: 0;
  margin-left: -15px;
}

.active .fasAnimate.fa-minus{
  transform: rotate(0deg);
  opacity: 1;
}

.feat-content {
  display: inline-block;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100px;
}

.header-track-section {
  background: #fff;
  overflow: hidden;
  border-radius: 5px !important;
}

.replace-audio-icon {
  position: absolute;
  top: 2px;
  z-index: 1;
  right: 3px;
}

.remove-featuring {
  position: absolute;
  top: 17px;
  right: 25px;
}

.remove-contributor-icon {
  position: absolute;
  bottom: 10px;
  right: 10px;
  padding: 2px 6px;
  transition: all 0.4s ease;
  color: #fff;
  background-color: #FF0000;
  box-shadow: 0px 1px 2px rgb(255, 255, 255);
  &:hover{
    background-color: #ff0000c9;
    box-shadow: 0px 1px 2px rgb(148, 148, 148);
  }
}

.add-featuring-icon {
  position: absolute;
  top: 17px;
  right: 25px;
}

.add-contributor-icon {
  position: absolute;
  top: 13px;
  right: 25px;
}

.text-ellipsis {
  overflow: hidden;
  text-overflow: ellipsis;
}

.track-title-container {
  width: 93%;
  cursor: pointer;
}

.track-buttons-container {
  width: 7%;
}

.duplicate-track {
  float: right;
}

.tooltip-content{
  h4 {
    font-size: 14px;
  }
  p {
    font-size: 12px;
    line-height: 20px;
    margin-top: 15px;
  }
}

.tooltip-label{
  float: right;
  margin-top: 5px;
}

.pd-row{
  padding: 0 15px;
}

.input-flat {
  border: 1px solid white;
  border-radius: 3px;
}

.price-big {
  font-weight: 900;
  font-size: 52px;
}

.audio-type{
  padding-top: 12px;
  padding-left: 15px;
}

.rds4{
  border-radius: 4px;
}

.contain-vocals {
  min-height: 90px;
}

.more-info-link {
  color: #5714ff;
  text-decoration: underline;
  padding-right: 30px;
  font-size: 14px;
  font-weight: lighter;

  &.mobile {
    display: none;
    padding: 0;
  }
}

.metadata-modified {
  box-shadow: 0px 0px 2px 0px #5714ff;
}

.text-modified {
  color: #5714ff;
}

.spotify-paginator {
  .paginator {
    width: 49%;
    display: inline-block;
    &.previous {
      text-align: left;
      .btn {
        color: #5714ff !important;
      }
    }
    &.next {
      text-align: right;
      .btn {
        color: #5714ff !important;
      }
    }
  }
}

@media only screen and (max-width: 767px)  {
  .modalFeaturings {
    .what-is {
      label {
        display: block;
      }
      .tooltip-label {
        float: none !important;
      }
    }
    .check-box-primary {
      position: absolute;
      right: 40px;
      width: calc(100% - 80px);
    }
  }
}

@media only screen and (max-width: 575px)  {
  .title-elemnt {
    padding-bottom: 15px;
  }
  .more-info-link {
    display: none;
    &.mobile {
      display: block;
      padding-top: 15px;
    }
  }
}

@media only screen and (max-width: 414px)  {
  .modalFeaturings {
    .check-box-primary {
      bottom: 0px;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/DistributionSongs.vue